import React, { useEffect, useState, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Table, Button, Modal, Form, Input, InputNumber, Select, DatePicker, Popconfirm, Switch, message, Space, Divider, Tag, Card, Collapse, Row, Col, Statistic, Upload, Spin, Descriptions, Image, Grid } from 'antd';
import { PlusOutlined, SearchOutlined, DeleteOutlined, FileTextOutlined, CheckCircleOutlined, FilterOutlined, ClearOutlined, EditOutlined, UploadOutlined, SwapOutlined, EyeOutlined, ThunderboltOutlined } from '@ant-design/icons';
import { alislarAPI, carilerAPI, malzemelerAPI, giderTipleriAPI, faturaOkumaAPI, kasalarAPI, islemlerAPI, malzemeBirimDonusumleriAPI, API_BASE_URL, alisVadeleriAPI } from '../services/api';
import { useLookups } from '../hooks/useLookups';
import BirimDonusumModal from '../components/BirimDonusumModal';
import { formatPara, paraInputProps } from '../utils/format';
import { negatifKayitOnayi } from '../utils/onay';
import { bosSecenekliOptions, secimeUyuyor } from '../utils/filtre';
import dayjs from 'dayjs';

function Alislar() {
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;
  const {
    birimler: birimlerRaw,
    birimOptions: birimler,
    malzemeTipiOptions: malzemeTipleri,
    kdvOranlari: kdvOranlariRaw,
  } = useLookups();

  const kdvOranlari = kdvOranlariRaw.map(k => ({ value: Number(k.oran), label: '%' + Number(k.oran) }));

  const idByAd = Object.fromEntries(birimlerRaw.map(b => [b.ad, b.id]));
  const adById = Object.fromEntries(birimlerRaw.map(b => [b.id, b.ad]));
  const birimGruplari = [
    [idByAd['kg'], idByAd['gr']].filter(Boolean),
    [idByAd['lt'], idByAd['ml']].filter(Boolean),
  ];

  const [birimDonusumleri, setBirimDonusumleri] = useState([]);
  const [donusumOpen, setDonusumOpen] = useState(false);
  const [donusumMalzeme, setDonusumMalzeme] = useState(null);

  // Sabit grup içi çarpan (gr↔kg, ml↔lt) — fromId'den toId'ye geçiş çarpanı
  const sabitCarpan = (fromId, toId) => {
    if (fromId === toId) return 1;
    const f = adById[fromId], t = adById[toId];
    if ((f === 'gr' && t === 'kg') || (f === 'ml' && t === 'lt')) return 0.001;
    if ((f === 'kg' && t === 'gr') || (f === 'lt' && t === 'ml')) return 1000;
    return null;
  };

  // Malzeme + dönüşüm tablosu birleşik seçilebilir birimler
  const getSecilebilenBirimler = (malzemelerList, malzemeId) => {
    const m = malzemelerList.find(mal => mal.id === malzemeId);
    if (!m) return birimler;
    const grup = birimGruplari.find(g => g.includes(m.birim_id));
    const grupBirimIds = grup ? [...grup] : [m.birim_id];
    const donusumEkIds = [];
    birimDonusumleri
      .filter(d => d.malzeme_id === malzemeId)
      .forEach(d => {
        donusumEkIds.push(d.kaynak_birim_id);
        const kaynakGrup = birimGruplari.find(g => g.includes(d.kaynak_birim_id));
        if (kaynakGrup) kaynakGrup.forEach(id => donusumEkIds.push(id));
        const hedefGrup = birimGruplari.find(g => g.includes(d.hedef_birim_id));
        if (hedefGrup) hedefGrup.forEach(id => donusumEkIds.push(id));
      });
    const tumIds = [...new Set([...grupBirimIds, ...donusumEkIds])];
    return birimler.filter(b => tumIds.includes(b.value));
  };

  // Stoklu kalemde miktar/birim_fiyat'ı malzemenin asıl birimine çevir.
  // Çevrilemezse null döner — çağıran hata verir.
  // Tutar (miktar × birim_fiyat) korunur — toplamCarpan ile çarpıp ters bölüyoruz.
  const donusturStokKalemi = (k) => {
    if (!k.malzeme_id || k.miktar == null) return k;
    const m = malzemeler.find(mal => mal.id === k.malzeme_id);
    if (!m) return k;
    const malzemeBirimId = m.birim_id;
    const secilenBirimId = k.birim_id;
    if (!secilenBirimId || secilenBirimId === malzemeBirimId) {
      return { ...k, birim_id: malzemeBirimId };
    }
    const grup = birimGruplari.find(g => g.includes(malzemeBirimId));
    const grupBirimIds = grup ? [...grup] : [malzemeBirimId];

    // Sabit grup içi (kg/gr veya lt/ml) — direkt çarpan yeterli
    if (grupBirimIds.includes(secilenBirimId)) {
      const sc = sabitCarpan(secilenBirimId, malzemeBirimId);
      if (sc != null) {
        return {
          ...k,
          miktar: parseFloat((Number(k.miktar) * sc).toFixed(6)),
          birim_id: malzemeBirimId,
          birim_fiyat: k.birim_fiyat != null ? parseFloat((Number(k.birim_fiyat) / sc).toFixed(6)) : k.birim_fiyat,
        };
      }
    }

    // Dönüşüm tablosundan ara: doğrudan kaynak=secilen
    let donusum = birimDonusumleri.find(
      d => d.malzeme_id === k.malzeme_id && d.kaynak_birim_id === secilenBirimId
    );
    let onCarpan = 1;

    // Doğrudan yoksa secilenBirim grubundaki diğer birim üzerinden
    if (!donusum) {
      const secilenGrup = birimGruplari.find(g => g.includes(secilenBirimId));
      if (secilenGrup) {
        for (const gbId of secilenGrup) {
          if (gbId === secilenBirimId) continue;
          donusum = birimDonusumleri.find(
            d => d.malzeme_id === k.malzeme_id && d.kaynak_birim_id === gbId
          );
          if (donusum) {
            const c = sabitCarpan(secilenBirimId, gbId);
            if (c != null) { onCarpan = c; break; }
            donusum = null;
          }
        }
      }
    }

    if (donusum) {
      let toplamCarpan = onCarpan * Number(donusum.carpan);
      const hedefBirimId = donusum.hedef_birim_id;
      if (hedefBirimId !== malzemeBirimId) {
        const sc = sabitCarpan(hedefBirimId, malzemeBirimId);
        if (sc == null) return null; // zincirlenemez
        toplamCarpan *= sc;
      }
      return {
        ...k,
        miktar: parseFloat((Number(k.miktar) * toplamCarpan).toFixed(6)),
        birim_id: malzemeBirimId,
        birim_fiyat: k.birim_fiyat != null ? parseFloat((Number(k.birim_fiyat) / toplamCarpan).toFixed(6)) : k.birim_fiyat,
      };
    }

    return null; // dönüşüm tanımlı değil
  };

  const [data, setData] = useState([]);
  const [cariler, setCariler] = useState([]);
  const [tumCariler, setTumCariler] = useState([]);
  const [malzemeler, setMalzemeler] = useState([]);
  const [giderTipleri, setGiderTipleri] = useState([]);
  const [kasalar, setKasalar] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [detayModalOpen, setDetayModalOpen] = useState(false);
  const [detayData, setDetayData] = useState(null);
  const [vadeModalOpen, setVadeModalOpen] = useState(false);
  const [vadeData, setVadeData] = useState({ kayit: null, vadeler: [] });
  const [duzenleModalOpen, setDuzenleModalOpen] = useState(false);
  const [duzenleData, setDuzenleData] = useState(null);
  const [duzenleKalemler, setDuzenleKalemler] = useState([]);
  const [duzenleForm] = Form.useForm();
  const [aramaMetni, setAramaMetni] = useState('');
  const [filtreCariIds, setFiltreCariIds] = useState([]);
  const [filtreGiderTipiIds, setFiltreGiderTipiIds] = useState([]);
  const [filtreOdemeDurumu, setFiltreOdemeDurumu] = useState([]);
  const [filtreTutarOp, setFiltreTutarOp] = useState(null);
  const [filtreTutarDeger, setFiltreTutarDeger] = useState(null);
  const [filtreTarihBaslangic, setFiltreTarihBaslangic] = useState(null);
  const [filtreTarihBitis, setFiltreTarihBitis] = useState(null);
  const [filtreAcik, setFiltreAcik] = useState([]);
  const [kalemler, setKalemler] = useState([]);
  const [hemenOdendi, setHemenOdendi] = useState(false);
  const [seciliKasa, setSeciliKasa] = useState(null);
  const [form] = Form.useForm();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [faturaModalOpen, setFaturaModalOpen] = useState(false);
  const [faturaResults, setFaturaResults] = useState([]);
  const [faturaKaydetLoading, setFaturaKaydetLoading] = useState(false);
  const [faturaOlusturulanMalzemeler, setFaturaOlusturulanMalzemeler] = useState([]);
  const [malzemeArama, setMalzemeArama] = useState('');
  const [malzemeModalOpen, setMalzemeModalOpen] = useState(false);
  const [malzemeForm] = Form.useForm();

  const fetchData = async () => {
    setLoading(true);
    try {
      const [faturaRes, carilerRes, malzemeRes, giderRes, kasaRes, odemelerRes, donusumRes] = await Promise.all([
        alislarAPI.getAll(),
        carilerAPI.getAll(),
        malzemelerAPI.getAll(),
        giderTipleriAPI.getAll(),
        kasalarAPI.getAll(),
        islemlerAPI.getAll({ islem_tipi_id: 9 }),
        malzemeBirimDonusumleriAPI.getAllSon(),
      ]);

      // Ödemeleri cariye göre grupla
      const odemelerByCari = {};
      (odemelerRes.data || []).forEach(o => {
        if (!o.cari_id) return;
        if (!odemelerByCari[o.cari_id]) odemelerByCari[o.cari_id] = [];
        odemelerByCari[o.cari_id].push(o);
      });

      // Alışları cariye göre grupla ve tarihe göre sırala (eskiden yeniye)
      const faturalar = faturaRes.data || [];
      const faturalarByCari = {};
      faturalar.forEach(f => {
        const cariId = f.cari_id || 0;
        if (!faturalarByCari[cariId]) faturalarByCari[cariId] = [];
        faturalarByCari[cariId].push(f);
      });

      // Her cari için ödemeleri en eski alıştan başlayarak dağıt.
      // Anlık ödendi (a.kasa_id IS NOT NULL): kayıt anında otomatik ödeme oluşmuş,
      // ödeme bu faturaya bağlanmış — FIFO havuzundan hem fatura hem ödeme hariç tutulur.
      const odenmeDurumuMap = {};
      Object.keys(faturalarByCari).forEach(cariId => {
        const cariFaturalarAll = faturalarByCari[cariId].sort((a, b) => (a.tarih || '').localeCompare(b.tarih || ''));
        const anlikFaturaIds = new Set(cariFaturalarAll.filter(f => f.kasa_id).map(f => f.id));

        // Anlık ödendi faturalarını ödendi=3 olarak işaretle
        cariFaturalarAll.forEach(f => {
          if (f.kasa_id) {
            const tutar = Number(f.toplam_tutar) || 0;
            odenmeDurumuMap[f.id] = { odendi: 3, odenen_tutar: tutar };
          }
        });

        // Otomatik ödemeleri FIFO havuzundan çıkar
        const cariOdemeler = (odemelerByCari[cariId] || []).filter(o => {
          if (!o.aciklama) return true;
          const m = o.aciklama.match(/^Alış #(\d+) - /);
          return !(m && anlikFaturaIds.has(Number(m[1])));
        });
        let kalanOdeme = cariOdemeler.reduce((t, o) => t + (Number(o.tutar) || 0), 0);

        cariFaturalarAll.filter(f => !f.kasa_id).forEach(f => {
          const tutar = Number(f.toplam_tutar) || 0;
          kalanOdeme = Math.round(kalanOdeme * 100) / 100;
          if (kalanOdeme >= tutar - 0.01) {
            odenmeDurumuMap[f.id] = { odendi: 1, odenen_tutar: tutar };
            kalanOdeme = Math.round((kalanOdeme - tutar) * 100) / 100;
          } else if (kalanOdeme > 0.01) {
            odenmeDurumuMap[f.id] = { odendi: 2, odenen_tutar: Math.round(kalanOdeme * 100) / 100 };
            kalanOdeme = 0;
          } else {
            odenmeDurumuMap[f.id] = { odendi: 0, odenen_tutar: 0 };
          }
        });
      });

      // Carisiz faturalar: kasa_id varsa anlık ödendi, yoksa backend odendi
      faturalar.filter(f => !f.cari_id).forEach(f => {
        if (f.kasa_id) {
          const tutar = Number(f.toplam_tutar) || 0;
          odenmeDurumuMap[f.id] = { odendi: 3, odenen_tutar: tutar };
        } else if (!odenmeDurumuMap[f.id]) {
          odenmeDurumuMap[f.id] = { odendi: f.odendi || 0, odenen_tutar: 0 };
        }
      });

      // Fatura verilerine ödeme durumunu ekle
      const enrichedData = faturalar.map(f => ({
        ...f,
        ...odenmeDurumuMap[f.id] || { odendi: f.odendi ? 1 : 0, odenen_tutar: 0 },
      }));

      setData(enrichedData);
      setTumCariler(carilerRes.data);
      setCariler(carilerRes.data.filter(c => c.aktif !== 0));
      setMalzemeler(malzemeRes.data);
      setGiderTipleri(giderRes.data);
      setKasalar(kasaRes.data.filter(k => k.aktif));
      setBirimDonusumleri(donusumRes.data || []);
    } catch (err) {
      message.error('Alışlar yüklenemedi: ' + (err.response?.data?.hata || err.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  useEffect(() => {
    const duzenleId = searchParams.get('duzenle');
    if (duzenleId && data.length > 0 && !duzenleModalOpen) {
      handleDuzenle(Number(duzenleId));
      navigate('/alislar', { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, searchParams]);

  useEffect(() => {
    if (duzenleModalOpen && duzenleData) {
      duzenleForm.setFieldsValue({
        cari_id: duzenleData.cari_id,
        tarih: duzenleData.tarih ? dayjs(duzenleData.tarih) : null,
        fatura_no: duzenleData.fatura_no,
        aciklama: duzenleData.aciklama,
      });
    }
  }, [duzenleModalOpen, duzenleData, duzenleForm]);

  const yeniMalzemeSatiri = () => ({
    key: Date.now() + Math.random(),
    malzeme_id: null,
    miktar: null,
    birim_id: null,
    birim_fiyat: null,
    birim_fiyat_dahil: null,
    tutar: 0,
  });

  const yeniKalem = () => ({
    key: Date.now(),
    gider_tipi_id: null,
    aciklama: '',
    tutar: 0,
    tutarHaric: 0,
    stoklu: false,
    kdv_orani: 0,
    malzemeSatirlari: [],
  });

  const birimAdiToId = (birimAdi) => {
    if (!birimAdi) return 5;
    const lower = birimAdi.toLowerCase();
    const eslesme = birimler.find(b => b.label === lower);
    return eslesme ? eslesme.value : 5;
  };

  const malzemeEslestir = (ad) => {
    if (!ad) return null;
    const lower = ad.toLowerCase();
    const eslesme = malzemeler.find(m => m.ad.toLowerCase() === lower);
    if (eslesme) return eslesme;
    const kismi = malzemeler.find(m => m.ad.toLowerCase().includes(lower) || lower.includes(m.ad.toLowerCase()));
    return kismi || null;
  };

  // Cari adı normalize: Türkçe karakter katlama, tire, noktalama, boşluk temizle
  const normalizeAd = (ad) => {
    if (!ad) return '';
    return ad
      .replace(/İ/g, 'i').replace(/I/g, 'i').replace(/ı/g, 'i')
      .replace(/Ş/g, 's').replace(/ş/g, 's')
      .replace(/Ğ/g, 'g').replace(/ğ/g, 'g')
      .replace(/Ü/g, 'u').replace(/ü/g, 'u')
      .replace(/Ö/g, 'o').replace(/ö/g, 'o')
      .replace(/Ç/g, 'c').replace(/ç/g, 'c')
      .toLowerCase()
      .replace(/[-–—]/g, ' ')
      .replace(/[.,;:'"()]/g, '')
      .replace(/\s+/g, ' ')
      .trim();
  };

  const cariEslestir = (ad) => {
    if (!ad) return null;
    const norm = normalizeAd(ad);
    // Tam eşleşme
    const tam = tumCariler.find(c => normalizeAd(c.ad) === norm);
    if (tam) return tam;
    // Normalize edilmiş includes
    const kismi = tumCariler.find(c => {
      const cNorm = normalizeAd(c.ad);
      return cNorm.includes(norm) || norm.includes(cNorm);
    });
    if (kismi) return kismi;
    // Kelime bazlı eşleşme: tüm kelimelerin %80'i eşleşirse
    const adKelimeleri = norm.split(' ').filter(w => w.length > 2);
    if (adKelimeleri.length > 0) {
      const kelimeEs = tumCariler.find(c => {
        const cKelimeleri = normalizeAd(c.ad).split(' ').filter(w => w.length > 2);
        const eslesenSayisi = adKelimeleri.filter(w => cKelimeleri.some(cw => cw.includes(w) || w.includes(cw))).length;
        return eslesenSayisi >= Math.ceil(Math.min(adKelimeleri.length, cKelimeleri.length) * 0.8);
      });
      if (kelimeEs) return kelimeEs;
    }
    return null;
  };

  const benzerCariBul = (ad) => {
    if (!ad || ad.length < 2) return [];
    const norm = normalizeAd(ad);
    const adKelimeleri = norm.split(' ').filter(w => w.length > 2);
    return tumCariler.filter(c => {
      const cNorm = normalizeAd(c.ad);
      if (cNorm.includes(norm) || norm.includes(cNorm)) return true;
      const cKelimeleri = cNorm.split(' ').filter(w => w.length > 2);
      return adKelimeleri.some(w => cKelimeleri.some(cw => cw.includes(w) || w.includes(cw)));
    }).slice(0, 10);
  };

  const benzerMalzemeBul = (ad) => {
    if (!ad || ad.length < 2) return [];
    const lower = ad.toLowerCase();
    return malzemeler.filter(m => {
      const mLower = m.ad.toLowerCase();
      return mLower.includes(lower) || lower.includes(mLower) ||
        mLower.split(' ').some(w => lower.includes(w) && w.length > 2) ||
        lower.split(' ').some(w => mLower.includes(w) && w.length > 2);
    }).slice(0, 10);
  };

  const faturaResultGuncelle = (entryId, field, value) => {
    setFaturaResults(prev => prev.map(r => {
      if (r.id !== entryId) return r;
      return { ...r, result: { ...r.result, [field]: value } };
    }));
  };

  const benzerKayitBul = (result) => {
    if (!result) return null;
    const faturaTarih = result.tarih ? dayjs(result.tarih).format('YYYY-MM-DD') : null;
    if (!faturaTarih) return null;
    const faturaToplam = (result.kalemler || []).reduce((t, k) => t + (Number(k.toplam) || (Number(k.miktar || 1) * Number(k.birim_fiyat || 0))), 0);
    const faturaToplam_kdvDahil = Number(result.genel_toplam_kdv_dahil) || faturaToplam;

    // Önce tam cari eşleşmesi ile dene
    const cariEs = cariEslestir(result.tedarikci);
    const adayCariIds = new Set();
    if (cariEs) adayCariIds.add(cariEs.id);

    // Benzer carileri de ekle
    const benzerler = benzerCariBul(result.tedarikci);
    benzerler.forEach(c => adayCariIds.add(c.id));

    if (adayCariIds.size === 0) return null;

    return data.find(d => {
      if (!adayCariIds.has(d.cari_id)) return false;
      const kayitTarih = dayjs(d.tarih).format('YYYY-MM-DD');
      if (kayitTarih !== faturaTarih) return false;
      const kayitTutar = Number(d.toplam_tutar || 0);
      if (kayitTutar === 0 && faturaToplam === 0) return true;
      // Hem KDV hariç hem KDV dahil toplamla karşılaştır
      const fark1 = Math.abs(kayitTutar - faturaToplam);
      const fark2 = Math.abs(kayitTutar - faturaToplam_kdvDahil);
      const minFark = Math.min(fark1, fark2);
      const oran = minFark / Math.max(kayitTutar, faturaToplam_kdvDahil, 1);
      return oran <= 0.2;
    }) || null;
  };

  // OCR sonucunu kalem yapısına çevir — aynı KDV oranı olanlar tek kalem, farklı KDV oranı ayrı kalem
  const ocrToKalemler = (result) => {
    const malzemeGiderTipi = giderTipleri.find(g => g.ad.toLowerCase() === 'malzeme');
    const defaultGiderTipiId = malzemeGiderTipi?.id || giderTipleri[0]?.id || null;
    const kusüratGiderTipi = giderTipleri.find(g => g.ad.toLowerCase() === 'küsürat düzeltme');

    // KDV oranına göre grupla
    const kdvGruplari = {};
    for (const k of (result.kalemler || [])) {
      const kdvOrani = [0, 1, 10, 20].includes(Number(k.kdv_orani)) ? Number(k.kdv_orani) : 10;
      if (!kdvGruplari[kdvOrani]) kdvGruplari[kdvOrani] = [];

      const malzemeEs = malzemeEslestir(k.malzeme);

      // OCR ambalaj bilgisi varsa (ör. "2 × 600 gr" → miktar=2, ambalaj_miktari=600, ambalaj_birimi="gr")
      // paket sayısını toplam miktara çevir ve faturadaki birimi ambalaj birimine al.
      let ocrMiktar = Number(k.miktar) || 1;
      let ocrBirimFiyat = Number(k.birim_fiyat) || 0;
      let ocrBirimAdi = k.birim;
      const ambalajMiktari = Number(k.ambalaj_miktari) || 0;
      if (ambalajMiktari > 0 && k.ambalaj_birimi) {
        const toplamFiyat = Number(k.toplam) || (ocrMiktar * ocrBirimFiyat);
        const toplamMiktar = ocrMiktar * ambalajMiktari;
        ocrMiktar = toplamMiktar;
        ocrBirimFiyat = toplamMiktar > 0 ? toplamFiyat / toplamMiktar : 0;
        ocrBirimAdi = k.ambalaj_birimi;
      }

      const faturaBirimId = birimAdiToId(ocrBirimAdi);
      let birimId = malzemeEs?.birim_id || faturaBirimId;
      let miktar = ocrMiktar;
      let birimFiyat = ocrBirimFiyat;

      // Birim uyuşmazlığı: otomatik çevir (kg↔gr, lt↔ml)
      if (malzemeEs && malzemeEs.birim_id !== faturaBirimId) {
        const malzemeBirim = malzemeEs.birim_id;
        // gr → kg: böl 1000
        if (faturaBirimId === 2 && malzemeBirim === 1) {
          miktar = miktar / 1000;
          birimFiyat = birimFiyat * 1000;
          birimId = 1;
        }
        // kg → gr: çarp 1000
        else if (faturaBirimId === 1 && malzemeBirim === 2) {
          miktar = miktar * 1000;
          birimFiyat = birimFiyat / 1000;
          birimId = 2;
        }
        // ml → lt: böl 1000
        else if (faturaBirimId === 4 && malzemeBirim === 3) {
          miktar = miktar / 1000;
          birimFiyat = birimFiyat * 1000;
          birimId = 3;
        }
        // lt → ml: çarp 1000
        else if (faturaBirimId === 3 && malzemeBirim === 4) {
          miktar = miktar * 1000;
          birimFiyat = birimFiyat / 1000;
          birimId = 4;
        }
      }

      miktar = Number(miktar.toFixed(3));
      birimFiyat = Number(birimFiyat.toFixed(4));

      kdvGruplari[kdvOrani].push({
        key: Date.now() + Math.random(),
        malzeme_id: malzemeEs?.id || null,
        malzeme_ad_ocr: k.malzeme || '',
        birim_ad_ocr: k.birim || 'adet',
        miktar,
        birim_id: birimId,
        birim_fiyat: birimFiyat,
        tutar: Number((miktar * birimFiyat).toFixed(2)),
      });
    }

    const kalemler = Object.entries(kdvGruplari).map(([kdvOrani, satirlar]) => {
      const toplamTutar = satirlar.reduce((t, s) => t + (s.tutar || 0), 0);
      return {
        key: Date.now() + Math.random(),
        gider_tipi_id: defaultGiderTipiId,
        aciklama: '',
        tutar: toplamTutar,
        stoklu: true,
        kdv_orani: Number(kdvOrani),
        malzemeSatirlari: satirlar,
      };
    });

    // Küsürat düzeltme: fatura genel toplamı ile hesaplanan toplam arasındaki fark
    const faturaKdvHaric = Number(result.genel_toplam_kdv_haric) || 0;
    const faturaKdv = Number(result.genel_toplam_kdv) || 0;
    if (faturaKdvHaric > 0 && kusüratGiderTipi) {
      let hesaplananKdvHaric = 0;
      let hesaplananKdv = 0;
      kalemler.forEach(k => {
        const kalemKdvHaric = k.stoklu
          ? k.malzemeSatirlari.reduce((t, s) => t + ((s.miktar || 0) * (s.birim_fiyat || 0)), 0)
          : (k.tutar || 0);
        const kalemKdv = Number((kalemKdvHaric * (k.kdv_orani || 0) / 100).toFixed(2));
        hesaplananKdvHaric += kalemKdvHaric;
        hesaplananKdv += kalemKdv;
      });

      const farkKdvHaric = Number((faturaKdvHaric - hesaplananKdvHaric).toFixed(2));
      const farkKdv = Number((faturaKdv - hesaplananKdv).toFixed(2));

      if (farkKdvHaric !== 0 || farkKdv !== 0) {
        kalemler.push({
          key: Date.now() + Math.random(),
          gider_tipi_id: kusüratGiderTipi.id,
          aciklama: 'Küsürat düzeltme',
          tutar: farkKdvHaric,
          stoklu: false,
          kdv_orani: null,
          kusurat_kdv_tutar: farkKdv,
          malzemeSatirlari: [],
        });
      }
    }

    return kalemler;
  };

  const handleFaturaYukle = async (file) => {
    const fileId = Date.now() + '_' + Math.random().toString(36).substr(2, 9);
    setFaturaResults(prev => [...prev, {
      id: fileId, fileName: file.name,
      status: 'uploading', result: null, error: null, gorsel_path: null, eslesme: null, guncellemeSec: null, kalemler: []
    }]);

    try {
      const res = await faturaOkumaAPI.oku(file);

      // XML / ZIP yüklemelerinde tek dosyadan birden çok fatura dönebilir
      if (res.data?.coklu) {
        const faturalar = res.data.faturalar || [];
        const yeniKayitlar = faturalar.map((fatura, i) => ({
          id: `${fileId}_${i}`,
          fileName: fatura.kaynak_dosya || file.name,
          status: 'done',
          result: fatura,
          error: null,
          gorsel_path: fatura.gorsel_path,
          eslesme: benzerKayitBul(fatura),
          guncellemeSec: benzerKayitBul(fatura) ? 'guncelle' : null,
          kalemler: ocrToKalemler(fatura),
        }));

        const hatalar = (res.data.hatalar || []).map((h, i) => ({
          id: `${fileId}_h${i}`,
          fileName: h.kaynak_dosya || file.name,
          status: 'error', result: null, error: h.hata,
          gorsel_path: null, eslesme: null, guncellemeSec: null, kalemler: []
        }));

        setFaturaResults(prev => [
          ...prev.filter(r => r.id !== fileId),
          ...yeniKayitlar,
          ...hatalar,
        ]);
        if (yeniKayitlar.length > 0) message.success(`${file.name}: ${yeniKayitlar.length} fatura okundu`);
        return false;
      }

      const eslesme = benzerKayitBul(res.data);
      const convertedKalemler = ocrToKalemler(res.data);
      setFaturaResults(prev => prev.map(r =>
        r.id === fileId
          ? { ...r, status: 'done', result: res.data, gorsel_path: res.data.gorsel_path, eslesme, guncellemeSec: eslesme ? 'guncelle' : null, kalemler: convertedKalemler }
          : r
      ));
    } catch (err) {
      setFaturaResults(prev => prev.map(r =>
        r.id === fileId
          ? { ...r, status: 'error', error: err.response?.data?.hata || err.message }
          : r
      ));
    }
    return false;
  };

  // Fatura entry kalem yönetim fonksiyonları
  const faturaKalemGuncelle = (entryId, kalemKey, field, value) => {
    setFaturaResults(prev => prev.map(r => {
      if (r.id !== entryId) return r;
      const yeniKalemler = r.kalemler.map(k => {
        if (k.key !== kalemKey) return k;
        const updated = { ...k, [field]: value };
        if (field === 'gider_tipi_id' && value) {
          const secilen = giderTipleri.find(g => g.id === value);
          if (secilen && secilen.ad.toLowerCase() === 'malzeme') {
            updated.stoklu = true;
            if (updated.malzemeSatirlari.length === 0) updated.malzemeSatirlari = [yeniMalzemeSatiri()];
          } else {
            updated.stoklu = false;
          }
        }
        if (field === 'stoklu') {
          if (value && updated.malzemeSatirlari.length === 0) updated.malzemeSatirlari = [yeniMalzemeSatiri()];
          if (!value) updated.malzemeSatirlari = [];
        }
        if (!updated.stoklu) {
          const oran = Number(updated.kdv_orani) || 0;
          if (field === 'tutar') {
            const dahil = Number(value) || 0;
            updated.tutarHaric = oran > 0 ? Number((dahil / (1 + oran / 100)).toFixed(2)) : dahil;
          } else if (field === 'tutarHaric') {
            const haric = Number(value) || 0;
            updated.tutar = oran > 0 ? Number((haric * (1 + oran / 100)).toFixed(2)) : haric;
          } else if (field === 'kdv_orani') {
            const dahil = Number(updated.tutar) || 0;
            updated.tutarHaric = oran > 0 ? Number((dahil / (1 + oran / 100)).toFixed(2)) : dahil;
          }
        }
        return updated;
      });
      return { ...r, kalemler: yeniKalemler };
    }));
  };

  const faturaMalzemeSatiriGuncelle = (entryId, kalemKey, satirKey, field, value) => {
    setFaturaResults(prev => prev.map(r => {
      if (r.id !== entryId) return r;
      const yeniKalemler = r.kalemler.map(k => {
        if (k.key !== kalemKey) return k;
        const yeniSatirlar = k.malzemeSatirlari.map(s => {
          if (s.key !== satirKey) return s;
          const updated = { ...s, [field]: value };
          if (field === 'malzeme_id' && value) {
            const m = malzemeler.find(mal => mal.id === value);
            if (m) { updated.birim_id = m.birim_id; }
          }
          if (field === 'miktar' || field === 'birim_fiyat') {
            const miktar = field === 'miktar' ? (value || 0) : (s.miktar || 0);
            const fiyat = field === 'birim_fiyat' ? (value || 0) : (s.birim_fiyat || 0);
            updated.tutar = miktar * fiyat;
          }
          return updated;
        });
        const toplamTutar = yeniSatirlar.reduce((t, s) => t + (s.tutar || 0), 0);
        return { ...k, malzemeSatirlari: yeniSatirlar, tutar: toplamTutar };
      });
      return { ...r, kalemler: yeniKalemler };
    }));
  };

  const faturaMalzemeSatiriEkle = (entryId, kalemKey) => {
    setFaturaResults(prev => prev.map(r => {
      if (r.id !== entryId) return r;
      return { ...r, kalemler: r.kalemler.map(k => k.key !== kalemKey ? k : { ...k, malzemeSatirlari: [...k.malzemeSatirlari, yeniMalzemeSatiri()] }) };
    }));
  };

  const faturaMalzemeSatiriSil = (entryId, kalemKey, satirKey) => {
    setFaturaResults(prev => prev.map(r => {
      if (r.id !== entryId) return r;
      return { ...r, kalemler: r.kalemler.map(k => k.key !== kalemKey ? k : { ...k, malzemeSatirlari: k.malzemeSatirlari.filter(s => s.key !== satirKey) }) };
    }));
  };

  const faturaKalemEkle = (entryId) => {
    setFaturaResults(prev => prev.map(r => r.id !== entryId ? r : { ...r, kalemler: [...r.kalemler, yeniKalem()] }));
  };

  const faturaKalemSil = (entryId, kalemKey) => {
    setFaturaResults(prev => prev.map(r => r.id !== entryId ? r : { ...r, kalemler: r.kalemler.filter(k => k.key !== kalemKey) }));
  };

  // Hızlı malzeme ekleme (fatura okumadan)
  const [faturaHizliMalzemeAd, setFaturaHizliMalzemeAd] = useState('');
  const [faturaHizliMalzemeOpen, setFaturaHizliMalzemeOpen] = useState(false);
  const [faturaHizliMalzemeCallback, setFaturaHizliMalzemeCallback] = useState(null);
  const [faturaHizliMalzemeForm] = Form.useForm();

  // Hızlı cari ekleme (fatura okumadan)
  const [faturaHizliCariOpen, setFaturaHizliCariOpen] = useState(false);
  const [faturaHizliCariCallback, setFaturaHizliCariCallback] = useState(null);
  const [faturaHizliCariForm] = Form.useForm();

  const cariTipleriFatura = [
    { value: 1, label: 'Tedarikçi' },
    { value: 2, label: 'Müşteri' },
    { value: 3, label: 'Personel' },
    { value: 4, label: 'Ortak' },
  ];

  const faturaHizliCariAc = (ad, callback) => {
    faturaHizliCariForm.setFieldsValue({ ad, tip_idler: [1] });
    setFaturaHizliCariCallback(() => callback);
    setFaturaHizliCariOpen(true);
  };

  const faturaHizliCariKaydet = async (values) => {
    try {
      const res = await carilerAPI.create(values);
      message.success(`"${values.ad}" carisi eklendi`);
      const carilerRes = await carilerAPI.getAll();
      setTumCariler(carilerRes.data);
      setCariler(carilerRes.data.filter(c => c.aktif !== 0));
      setFaturaHizliCariOpen(false);
      faturaHizliCariForm.resetFields();
      if (faturaHizliCariCallback) faturaHizliCariCallback(values.ad);
    } catch (err) {
      message.error('Cari eklenemedi: ' + (err.response?.data?.hata || err.message));
    }
  };

  const faturaHizliMalzemeAc = (ad, birimAd, callback) => {
    setFaturaHizliMalzemeAd(ad);
    const birimId = birimAd ? birimAdiToId(birimAd) : 5;
    faturaHizliMalzemeForm.setFieldsValue({ ad, birim_id: birimId, malzeme_tipi_id: 3 });
    setFaturaHizliMalzemeCallback(() => callback);
    setFaturaHizliMalzemeOpen(true);
  };

  const faturaHizliMalzemeKaydet = async (values) => {
    try {
      const res = await malzemelerAPI.create(values);
      message.success(`"${values.ad}" malzemesi eklendi`);
      setFaturaOlusturulanMalzemeler(prev => [...prev, res.data.id]);
      const malzemeRes = await malzemelerAPI.getAll();
      setMalzemeler(malzemeRes.data);
      setFaturaHizliMalzemeOpen(false);
      faturaHizliMalzemeForm.resetFields();
      if (faturaHizliMalzemeCallback) faturaHizliMalzemeCallback(res.data.id);
    } catch (err) {
      message.error('Malzeme eklenemedi: ' + (err.response?.data?.hata || err.message));
    }
  };

  const faturaModalKapat = async () => {
    // Kaydedilmemiş faturalardaki oluşturulan malzemeleri sil
    if (faturaOlusturulanMalzemeler.length > 0) {
      // Kaydedilmiş faturalardaki malzeme ID'lerini bul
      const kaydedilenMalzemeIds = new Set();
      faturaResults.filter(r => r.status === 'saved').forEach(r => {
        (r.kalemler || []).forEach(k => {
          (k.malzemeSatirlari || []).forEach(s => {
            if (s.malzeme_id) kaydedilenMalzemeIds.add(s.malzeme_id);
          });
        });
      });

      // Kaydedilmemiş olanları sil
      const silinecekler = faturaOlusturulanMalzemeler.filter(id => !kaydedilenMalzemeIds.has(id));
      for (const id of silinecekler) {
        try { await malzemelerAPI.remove(id); } catch {}
      }
      if (silinecekler.length > 0) {
        const malzemeRes = await malzemelerAPI.getAll();
        setMalzemeler(malzemeRes.data);
      }
    }
    setFaturaOlusturulanMalzemeler([]);
    setFaturaModalOpen(false);
    setFaturaResults([]);
  };

  const faturaKaydetTek = async (entry) => {
    const cariEs = cariEslestir(entry.result.tedarikci);
    if (!cariEs) {
      message.error(`"${entry.result.tedarikci || '(boş)'}" için cari eşleşmedi. Lütfen mevcut bir cari seçin veya yeni cari oluşturun.`);
      throw new Error('Cari eşleşmedi');
    }
    // Eşleşmeyen malzeme kontrolü
    const eslesmeyenMalzeme = entry.kalemler.flatMap(k => k.malzemeSatirlari || []).find(s => !s.malzeme_id);
    if (eslesmeyenMalzeme) {
      message.error(`"${eslesmeyenMalzeme.malzeme_ad_ocr || '(boş)'}" malzemesi eşleşmedi. Lütfen mevcut malzeme seçin veya yeni oluşturun.`);
      throw new Error('Malzeme eşleşmedi');
    }
    const ortakPayload = {
      cari_id: cariEs?.id || null,
      tarih: entry.result.tarih || dayjs().format('YYYY-MM-DD'),
      fatura_no: entry.result.fatura_no || '',
      aciklama: 'Faturadan otomatik eklendi',
      gorsel_path: entry.gorsel_path || null,
    };

    // Güncelleme modunda eski mantık: ilk kalem update'lenir (tek alış); kalan kalemler için yeni alış oluşturur.
    if (entry.guncellemeSec === 'guncelle' && entry.eslesme) {
      const [ilk, ...digerleri] = entry.kalemler;
      const ilkBackend = kalemleriBackendFormatinaCevir([ilk]);
      await alislarAPI.update(entry.eslesme.id, {
        ...ortakPayload,
        gider_tipi_id: ilk.gider_tipi_id,
        kalemler: ilkBackend,
      });
      for (const k of digerleri) {
        const tekBackend = kalemleriBackendFormatinaCevir([k]);
        await alislarAPI.create({
          ...ortakPayload,
          gider_tipi_id: k.gider_tipi_id,
          kalemler: tekBackend,
        });
      }
      return entry.kalemler.length;
    }

    // Create modunda: her kalem ayrı alış
    let tamamlanan = 0;
    for (const k of entry.kalemler) {
      const tekBackend = kalemleriBackendFormatinaCevir([k]);
      await alislarAPI.create({
        ...ortakPayload,
        gider_tipi_id: k.gider_tipi_id,
        kalemler: tekBackend,
      });
      tamamlanan++;
    }
    return tamamlanan;
  };

  const handleFaturaKaydetTek = async (entryId) => {
    const entry = faturaResults.find(r => r.id === entryId);
    if (!entry || entry.status !== 'done' || !entry.result?.kalemler?.length) return;

    setFaturaResults(prev => prev.map(r => r.id === entryId ? { ...r, status: 'saving' } : r));
    try {
      const kalemSayisi = await faturaKaydetTek(entry);
      message.success(`${kalemSayisi} kalemli alış kaydedildi`);
      // Kaydedilen faturadaki malzemeleri takip listesinden çıkar (artık kalıcı)
      const kaydedilenMalzemeIds = new Set();
      (entry.kalemler || []).forEach(k => (k.malzemeSatirlari || []).forEach(s => { if (s.malzeme_id) kaydedilenMalzemeIds.add(s.malzeme_id); }));
      setFaturaOlusturulanMalzemeler(prev => prev.filter(id => !kaydedilenMalzemeIds.has(id)));
      setFaturaResults(prev => prev.map(r => r.id === entryId ? { ...r, status: 'saved' } : r));
      fetchData();
    } catch (err) {
      message.error('Kaydedilemedi: ' + (err.response?.data?.hata || err.message));
      setFaturaResults(prev => prev.map(r => r.id === entryId ? { ...r, status: 'done' } : r));
    }
  };

  const handleFaturaTumunuKaydet = async () => {
    const kayitEdilecekler = faturaResults.filter(r => r.status === 'done' && r.result?.kalemler?.length > 0);
    if (kayitEdilecekler.length === 0) return;
    setFaturaKaydetLoading(true);
    let basarili = 0;
    for (const entry of kayitEdilecekler) {
      setFaturaResults(prev => prev.map(r => r.id === entry.id ? { ...r, status: 'saving' } : r));
      try {
        await faturaKaydetTek(entry);
        setFaturaResults(prev => prev.map(r => r.id === entry.id ? { ...r, status: 'saved' } : r));
        basarili++;
      } catch {
        setFaturaResults(prev => prev.map(r => r.id === entry.id ? { ...r, status: 'done' } : r));
      }
    }
    setFaturaKaydetLoading(false);
    if (basarili > 0) {
      message.success(`${basarili} fatura kaydedildi`);
      fetchData();
    }
  };

  const handleFaturaSil = (entryId) => {
    setFaturaResults(prev => prev.filter(r => r.id !== entryId));
  };

  const handleModalAc = () => {
    setKalemler([yeniKalem()]);
    setHemenOdendi(false);
    setSeciliKasa(null);
    form.resetFields();
    setModalOpen(true);
  };

  const kalemEkle = () => {
    setKalemler([...kalemler, yeniKalem()]);
  };

  const kalemSil = (key) => {
    setKalemler(kalemler.filter(k => k.key !== key));
  };

  const kalemGuncelle = (key, field, value) => {
    setKalemler(kalemler.map(k => {
      if (k.key !== key) return k;
      const updated = { ...k, [field]: value };

      if (field === 'gider_tipi_id' && value) {
        const secilen = giderTipleri.find(g => g.id === value);
        if (secilen && secilen.ad.toLowerCase() === 'malzeme') {
          updated.stoklu = true;
          if (updated.malzemeSatirlari.length === 0) {
            updated.malzemeSatirlari = [yeniMalzemeSatiri()];
          }
        } else {
          updated.stoklu = false;
        }
      }

      if (field === 'stoklu') {
        if (value && updated.malzemeSatirlari.length === 0) {
          updated.malzemeSatirlari = [yeniMalzemeSatiri()];
        }
        if (!value) {
          updated.malzemeSatirlari = [];
        }
      }

      if (!updated.stoklu) {
        const oran = Number(updated.kdv_orani) || 0;
        if (field === 'tutar') {
          const dahil = Number(value) || 0;
          updated.tutarHaric = oran > 0 ? Number((dahil / (1 + oran / 100)).toFixed(2)) : dahil;
        } else if (field === 'tutarHaric') {
          const haric = Number(value) || 0;
          updated.tutar = oran > 0 ? Number((haric * (1 + oran / 100)).toFixed(2)) : haric;
        } else if (field === 'kdv_orani') {
          const dahil = Number(updated.tutar) || 0;
          updated.tutarHaric = oran > 0 ? Number((dahil / (1 + oran / 100)).toFixed(2)) : dahil;
        }
      }

      // Stoklu kalemde KDV oranı değişince malzeme satırlarının KDV dahil birim fiyatını güncelle
      if (updated.stoklu && field === 'kdv_orani') {
        const oran = Number(value) || 0;
        updated.malzemeSatirlari = updated.malzemeSatirlari.map(s => {
          const haric = Number(s.birim_fiyat) || 0;
          return { ...s, birim_fiyat_dahil: oran > 0 ? Number((haric * (1 + oran / 100)).toFixed(2)) : haric };
        });
      }

      return updated;
    }));
  };

  const malzemeSatiriEkle = (kalemKey) => {
    setKalemler(kalemler.map(k => {
      if (k.key !== kalemKey) return k;
      return { ...k, malzemeSatirlari: [...k.malzemeSatirlari, yeniMalzemeSatiri()] };
    }));
  };

  const malzemeSatiriSil = (kalemKey, satirKey) => {
    setKalemler(kalemler.map(k => {
      if (k.key !== kalemKey) return k;
      return { ...k, malzemeSatirlari: k.malzemeSatirlari.filter(s => s.key !== satirKey) };
    }));
  };

  const malzemeSatiriGuncelle = (kalemKey, satirKey, field, value) => {
    setKalemler(kalemler.map(k => {
      if (k.key !== kalemKey) return k;
      const oran = Number(k.kdv_orani) || 0;
      const yeniSatirlar = k.malzemeSatirlari.map(s => {
        if (s.key !== satirKey) return s;
        const updated = { ...s, [field]: value };

        if (field === 'malzeme_id' && value) {
          const m = malzemeler.find(mal => mal.id === value);
          if (m) updated.birim_id = m.birim_id;
        }

        // Birim fiyat iki yönlü: hariç girilirse dahili, dahil girilirse hariçi hesapla
        if (field === 'birim_fiyat') {
          const haric = Number(value) || 0;
          updated.birim_fiyat_dahil = oran > 0 ? Number((haric * (1 + oran / 100)).toFixed(2)) : haric;
        } else if (field === 'birim_fiyat_dahil') {
          const dahil = Number(value) || 0;
          updated.birim_fiyat = oran > 0 ? Number((dahil / (1 + oran / 100)).toFixed(2)) : dahil;
        }

        if (field === 'miktar' || field === 'birim_fiyat' || field === 'birim_fiyat_dahil') {
          const miktar = field === 'miktar' ? value : updated.miktar;
          const fiyat = updated.birim_fiyat;
          if (miktar && fiyat) updated.tutar = Number((miktar * fiyat).toFixed(2));
        }

        return updated;
      });
      return { ...k, malzemeSatirlari: yeniSatirlar };
    }));
  };

  const kalemKdvHaricTutar = (k) => {
    // Stoklu: malzeme satırları zaten KDV hariç tutar
    // Stoksuz: girilen tutar KDV dahil, ters hesapla
    if (k.stoklu) {
      return k.malzemeSatirlari.reduce((st, s) => st + (Number(s.tutar) || 0), 0);
    }
    const dahil = Number(k.tutar) || 0;
    const oran = k.kdv_orani || 0;
    if (oran === 0) return dahil;
    return Number((dahil / (1 + oran / 100)).toFixed(2));
  };

  const kalemKdvDahilTutar = (k) => {
    if (k.stoklu) {
      // KDV hariç toplam + KDV oranı
      const haric = kalemKdvHaricTutar(k);
      const oran = k.kdv_orani || 0;
      return Number((haric * (1 + oran / 100)).toFixed(2));
    }
    // Stoksuz: girilen tutar zaten KDV dahil
    return Number(k.tutar) || 0;
  };

  const kalemKdvTutar = (k) => {
    return Number((kalemKdvDahilTutar(k) - kalemKdvHaricTutar(k)).toFixed(2));
  };

  const kalemlerKdvHaric = kalemler.reduce((t, k) => t + kalemKdvHaricTutar(k), 0);
  const kalemlerKdv = kalemler.reduce((t, k) => t + kalemKdvTutar(k), 0);
  const kalemlerToplam = kalemler.reduce((t, k) => t + kalemKdvDahilTutar(k), 0);

  const kalemleriBackendFormatinaCevir = (kalemlerData) => {
    const backendKalemler = [];
    kalemlerData.forEach(k => {
      // Küsürat düzeltme kalemi: kdv_orani null, tutar ve kdv ayrı ayrı belirtilmiş
      if (k.kdv_orani === null && k.kusurat_kdv_tutar !== undefined) {
        const haric = Number(k.tutar) || 0;
        const kdv = Number(k.kusurat_kdv_tutar) || 0;
        backendKalemler.push({
          gider_tipi_id: k.gider_tipi_id,
          aciklama: k.aciklama || 'Küsürat düzeltme',
          tutar: haric,
          kdv_tutar: kdv,
          kdv_dahil_tutar: Number((haric + kdv).toFixed(2)),
          stok_takibi: false,
          malzeme_id: null,
          miktar: null,
          birim_id: null,
          birim_fiyat: null,
        });
        return;
      }

      const dahil = kalemKdvDahilTutar(k);
      const haric = kalemKdvHaricTutar(k);
      const kdv = kalemKdvTutar(k);
      if (k.stoklu && k.malzemeSatirlari.length > 0) {
        const stokToplam = k.malzemeSatirlari.reduce((t, s) => t + (Number(s.tutar) || 0), 0);
        k.malzemeSatirlari.forEach(s => {
          const oran = stokToplam > 0 ? (s.tutar || 0) / stokToplam : 0;
          const cevrilmis = donusturStokKalemi({
            malzeme_id: s.malzeme_id,
            miktar: Number(s.miktar) || 0,
            birim_id: s.birim_id,
            birim_fiyat: Number(s.birim_fiyat) || 0,
          });
          if (cevrilmis === null) {
            const mal = malzemeler.find(mm => mm.id === s.malzeme_id);
            throw new Error(`${mal?.ad || 'Malzeme'} için ${adById[s.birim_id] || '?'} → ${adById[mal?.birim_id] || '?'} dönüşümü tanımlı değil. Önce birim dönüşümü ekleyin.`);
          }
          backendKalemler.push({
            gider_tipi_id: k.gider_tipi_id,
            aciklama: k.aciklama,
            tutar: Number((haric * oran).toFixed(2)),
            kdv_tutar: Number((kdv * oran).toFixed(2)),
            kdv_dahil_tutar: Number((dahil * oran).toFixed(2)),
            stok_takibi: true,
            malzeme_id: cevrilmis.malzeme_id,
            miktar: cevrilmis.miktar,
            birim_id: cevrilmis.birim_id,
            birim_fiyat: cevrilmis.birim_fiyat,
          });
        });
      } else {
        backendKalemler.push({
          gider_tipi_id: k.gider_tipi_id,
          aciklama: k.aciklama,
          tutar: haric,
          kdv_tutar: kdv,
          kdv_dahil_tutar: dahil,
          stok_takibi: false,
          malzeme_id: null,
          miktar: null,
          birim_id: null,
          birim_fiyat: null,
        });
      }
    });
    return backendKalemler;
  };

  const handleSubmit = async (values) => {
    if (kalemler.length === 0) {
      message.error('En az bir kalem ekleyin');
      return;
    }

    const gecersiz = kalemler.find(k => !k.gider_tipi_id);
    if (gecersiz) {
      message.error('Her kalemde gider tipi zorunludur');
      return;
    }

    const stokBosHatali = kalemler.find(k => k.stoklu && k.malzemeSatirlari.length === 0);
    if (stokBosHatali) {
      message.error('Stoklu kalemde en az bir malzeme satırı olmalı (veya Stok switchini kapatın)');
      return;
    }

    const stokHatali = kalemler.find(k => k.stoklu && k.malzemeSatirlari.some(s => !s.malzeme_id || !s.miktar || !s.birim_id || !s.birim_fiyat));
    if (stokHatali) {
      message.error('Malzeme satırlarında malzeme, miktar, birim ve birim fiyat zorunludur');
      return;
    }

    const tutarHatali = kalemler.find(k => !k.stoklu && !k.tutar);
    if (tutarHatali) {
      message.error('Stoksuz kalemlerde tutar zorunludur');
      return;
    }

    if (hemenOdendi && !seciliKasa) {
      message.error('Hemen ödendi seçili ise kasa seçimi zorunludur');
      return;
    }

    if (kalemler.some(k => kalemKdvDahilTutar(k) < 0) && !(await negatifKayitOnayi('alış'))) return;

    // Her UI kalemi ayrı bir alış kaydı olarak gönderilir (best-effort).
    // Stoklu kalem birden fazla malzeme satırı içerebilir — bunlar tek alış'a kalır.
    const ortakPayload = {
      cari_id: values.cari_id,
      tarih: values.tarih?.format('YYYY-MM-DD'),
      fatura_no: values.fatura_no,
      aciklama: values.aciklama,
      hemen_odendi: hemenOdendi,
      kasa_id: hemenOdendi ? seciliKasa : null,
    };

    let basarili = 0;
    const hatalilar = [];
    for (let i = 0; i < kalemler.length; i++) {
      const k = kalemler[i];
      try {
        const tekKalemBackend = kalemleriBackendFormatinaCevir([k]);
        const payload = {
          ...ortakPayload,
          gider_tipi_id: k.gider_tipi_id,
          kalemler: tekKalemBackend,
        };
        await alislarAPI.create(payload);
        basarili++;
      } catch (err) {
        hatalilar.push(`Kalem ${i + 1}: ${err.response?.data?.hata || err.message}`);
      }
    }

    if (basarili > 0 && hatalilar.length === 0) {
      message.success(`${basarili} alış kaydı oluşturuldu${hemenOdendi ? ' ve ödeme(ler) oluşturuldu' : ''}`);
      setModalOpen(false);
      setKalemler([]);
      form.resetFields();
      fetchData();
    } else if (basarili > 0) {
      message.warning(`${basarili} başarılı, ${hatalilar.length} hatalı. Hatalılar: ${hatalilar.join(' | ')}`);
      fetchData();
    } else {
      message.error(`Hiç kayıt oluşturulamadı: ${hatalilar.join(' | ')}`);
    }
  };

  const handleDetay = async (id) => {
    try {
      const res = await alislarAPI.getById(id);
      setDetayData(res.data);
      setDetayModalOpen(true);
    } catch (err) {
      message.error('Detay yüklenemedi');
    }
  };

  const handleDuzenle = async (id) => {
    try {
      const res = await alislarAPI.getById(id);
      const fatura = res.data;
      setDuzenleData(fatura);
      // KDV oranını tutar ve kdv_tutar'dan hesapla
      const kdvOraniHesapla = (tutar, kdvTutar) => {
        const t = Number(tutar) || 0;
        const k = Number(kdvTutar) || 0;
        if (t === 0 || k === 0) return 0;
        const oran = Math.round((k / t) * 100);
        // En yakın geçerli orana yuvarla
        if (oran <= 0) return 0;
        if (oran <= 1) return 1;
        if (oran <= 10) return 10;
        return 20;
      };

      // Backend kalemlerini grupla: aynı gider_tipi_id ve kdv_orani olan stoklu kalemleri birleştir.
      // NOT: alis_kalemleri.stok_takibi alanı artık kullanılmıyor (backend her zaman 0 yazıyor),
      // bu yüzden stoklu/stoksuz ayrımı malzeme_id'nin var olup olmamasına göre yapılır.
      const kalemGruplari = [];
      (fatura.kalemler || []).forEach(k => {
        const kdvOrani = kdvOraniHesapla(k.tutar, k.kdv_tutar);
        const isStoklu = !!k.malzeme_id;
        if (isStoklu) {
          let mevcut = kalemGruplari.find(g => g.gider_tipi_id === k.gider_tipi_id && g.stoklu && g.kdv_orani === kdvOrani);
          if (!mevcut) {
            mevcut = {
              key: Date.now() + Math.random(),
              gider_tipi_id: k.gider_tipi_id,
              aciklama: k.aciklama || '',
              tutar: 0,
              stoklu: true,
              kdv_orani: kdvOrani,
              malzemeSatirlari: [],
            };
            kalemGruplari.push(mevcut);
          }
          mevcut.malzemeSatirlari.push({
            key: Date.now() + Math.random(),
            malzeme_id: k.malzeme_id,
            miktar: k.miktar != null ? Number(k.miktar) : null,
            birim_id: k.birim_id,
            birim_fiyat: k.birim_fiyat != null ? Number(k.birim_fiyat) : null,
            birim_fiyat_dahil: k.birim_fiyat != null ? Number((Number(k.birim_fiyat) * (1 + (kdvOrani || 0) / 100)).toFixed(2)) : null,
            tutar: Number(k.tutar) || 0,
          });
        } else {
          // Stoksuz kalem: UI state'inde `tutar` KDV dahil tutuluyor (kalemKdvDahilTutar).
          // Backend kdv_dahil_tutar varsa onu, yoksa tutar+kdv_tutar'ı kullan.
          const kdvDahil = Number(k.kdv_dahil_tutar) || (Number(k.tutar) + Number(k.kdv_tutar || 0));
          const kdvHaric = Number(k.tutar) || 0;
          kalemGruplari.push({
            key: Date.now() + Math.random(),
            gider_tipi_id: k.gider_tipi_id,
            aciklama: k.aciklama || '',
            tutar: kdvDahil,
            tutarHaric: kdvHaric,
            stoklu: false,
            kdv_orani: kdvOrani,
            malzemeSatirlari: [],
          });
        }
      });
      setDuzenleKalemler(kalemGruplari);
      setDuzenleModalOpen(true);
    } catch (err) {
      message.error('Alış yüklenemedi: ' + (err.response?.data?.hata || err.message));
    }
  };

  const duzenleKalemEkle = () => {
    setDuzenleKalemler([...duzenleKalemler, yeniKalem()]);
  };

  const duzenleKalemSil = (key) => {
    setDuzenleKalemler(duzenleKalemler.filter(k => k.key !== key));
  };

  const duzenleKalemGuncelle = (key, field, value) => {
    setDuzenleKalemler(duzenleKalemler.map(k => {
      if (k.key !== key) return k;
      const updated = { ...k, [field]: value };

      if (field === 'gider_tipi_id' && value) {
        const secilen = giderTipleri.find(g => g.id === value);
        if (secilen && secilen.ad.toLowerCase() === 'malzeme') {
          updated.stoklu = true;
          if (updated.malzemeSatirlari.length === 0) {
            updated.malzemeSatirlari = [yeniMalzemeSatiri()];
          }
        } else {
          updated.stoklu = false;
        }
      }

      if (field === 'stoklu') {
        if (value && updated.malzemeSatirlari.length === 0) {
          updated.malzemeSatirlari = [yeniMalzemeSatiri()];
        }
        if (!value) {
          updated.malzemeSatirlari = [];
        }
      }

      if (!updated.stoklu) {
        const oran = Number(updated.kdv_orani) || 0;
        if (field === 'tutar') {
          const dahil = Number(value) || 0;
          updated.tutarHaric = oran > 0 ? Number((dahil / (1 + oran / 100)).toFixed(2)) : dahil;
        } else if (field === 'tutarHaric') {
          const haric = Number(value) || 0;
          updated.tutar = oran > 0 ? Number((haric * (1 + oran / 100)).toFixed(2)) : haric;
        } else if (field === 'kdv_orani') {
          const dahil = Number(updated.tutar) || 0;
          updated.tutarHaric = oran > 0 ? Number((dahil / (1 + oran / 100)).toFixed(2)) : dahil;
        }
      }

      // Stoklu kalemde KDV oranı değişince malzeme satırlarının KDV dahil birim fiyatını güncelle
      if (updated.stoklu && field === 'kdv_orani') {
        const oran = Number(value) || 0;
        updated.malzemeSatirlari = updated.malzemeSatirlari.map(s => {
          const haric = Number(s.birim_fiyat) || 0;
          return { ...s, birim_fiyat_dahil: oran > 0 ? Number((haric * (1 + oran / 100)).toFixed(2)) : haric };
        });
      }

      return updated;
    }));
  };

  const duzenleMalzemeSatiriEkle = (kalemKey) => {
    setDuzenleKalemler(duzenleKalemler.map(k => {
      if (k.key !== kalemKey) return k;
      return { ...k, malzemeSatirlari: [...k.malzemeSatirlari, yeniMalzemeSatiri()] };
    }));
  };

  const duzenleMalzemeSatiriSil = (kalemKey, satirKey) => {
    setDuzenleKalemler(duzenleKalemler.map(k => {
      if (k.key !== kalemKey) return k;
      return { ...k, malzemeSatirlari: k.malzemeSatirlari.filter(s => s.key !== satirKey) };
    }));
  };

  const duzenleMalzemeSatiriGuncelle = (kalemKey, satirKey, field, value) => {
    setDuzenleKalemler(duzenleKalemler.map(k => {
      if (k.key !== kalemKey) return k;
      const oran = Number(k.kdv_orani) || 0;
      const yeniSatirlar = k.malzemeSatirlari.map(s => {
        if (s.key !== satirKey) return s;
        const updated = { ...s, [field]: value };

        if (field === 'malzeme_id' && value) {
          const m = malzemeler.find(mal => mal.id === value);
          if (m) updated.birim_id = m.birim_id;
        }

        // Birim fiyat iki yönlü: hariç girilirse dahili, dahil girilirse hariçi hesapla
        if (field === 'birim_fiyat') {
          const haric = Number(value) || 0;
          updated.birim_fiyat_dahil = oran > 0 ? Number((haric * (1 + oran / 100)).toFixed(2)) : haric;
        } else if (field === 'birim_fiyat_dahil') {
          const dahil = Number(value) || 0;
          updated.birim_fiyat = oran > 0 ? Number((dahil / (1 + oran / 100)).toFixed(2)) : dahil;
        }

        if (field === 'miktar' || field === 'birim_fiyat' || field === 'birim_fiyat_dahil') {
          const miktar = field === 'miktar' ? value : updated.miktar;
          const fiyat = updated.birim_fiyat;
          if (miktar && fiyat) updated.tutar = Number((miktar * fiyat).toFixed(2));
        }

        return updated;
      });
      return { ...k, malzemeSatirlari: yeniSatirlar };
    }));
  };

  const duzenleKalemlerKdvHaric = duzenleKalemler.reduce((t, k) => t + kalemKdvHaricTutar(k), 0);
  const duzenleKalemlerKdv = duzenleKalemler.reduce((t, k) => t + kalemKdvTutar(k), 0);
  const duzenleKalemlerToplam = duzenleKalemler.reduce((t, k) => t + kalemKdvDahilTutar(k), 0);

  const handleDuzenleSubmit = async (values) => {
    if (duzenleKalemler.length === 0) {
      message.error('En az bir kalem ekleyin');
      return;
    }

    const gecersiz = duzenleKalemler.find(k => !k.gider_tipi_id);
    if (gecersiz) {
      message.error('Her kalemde gider tipi zorunludur');
      return;
    }

    const stokBosHatali = duzenleKalemler.find(k => k.stoklu && k.malzemeSatirlari.length === 0);
    if (stokBosHatali) {
      message.error('Stoklu kalemde en az bir malzeme satırı olmalı (veya Stok switchini kapatın)');
      return;
    }

    const stokHatali = duzenleKalemler.find(k => k.stoklu && k.malzemeSatirlari.some(s => !s.malzeme_id || !s.miktar || !s.birim_id || !s.birim_fiyat));
    if (stokHatali) {
      message.error('Malzeme satırlarında malzeme, miktar, birim ve birim fiyat zorunludur');
      return;
    }

    const tutarHatali = duzenleKalemler.find(k => !k.stoklu && !k.tutar);
    if (tutarHatali) {
      message.error('Stoksuz kalemlerde tutar zorunludur');
      return;
    }

    if (duzenleKalemler.some(k => kalemKdvDahilTutar(k) < 0) && !(await negatifKayitOnayi('alış'))) return;

    try {
      const payload = {
        cari_id: values.cari_id,
        tarih: values.tarih?.format('YYYY-MM-DD'),
        fatura_no: values.fatura_no,
        aciklama: values.aciklama,
        kalemler: kalemleriBackendFormatinaCevir(duzenleKalemler),
      };
      await alislarAPI.update(duzenleData.id, payload);
      message.success('Alış güncellendi');
      setDuzenleModalOpen(false);
      setDuzenleKalemler([]);
      duzenleForm.resetFields();
      fetchData();
    } catch (err) {
      message.error('Güncellenemedi: ' + (err.response?.data?.hata || err.message));
    }
  };

  const handleDelete = async (id) => {
    try {
      await alislarAPI.remove(id);
      message.success('Alış silindi');
      fetchData();
    } catch (err) {
      message.error('Silinemedi: ' + (err.response?.data?.hata || err.message));
    }
  };

  const cariFilterOptions = useMemo(() => {
    const map = new Map();
    data.forEach(d => { if (d.cari_id && d.cari) map.set(d.cari_id, d.cari); });
    const list = Array.from(map, ([value, label]) => ({ value, label })).sort((a, b) => a.label.localeCompare(b.label, 'tr'));
    return bosSecenekliOptions(list, data, 'cari_id');
  }, [data]);

  const giderTipiFilterOptions = useMemo(() => {
    const set = new Set();
    data.forEach(d => { if (d.gider_tipi_id) set.add(d.gider_tipi_id); });
    const list = giderTipleri.filter(g => set.has(g.id)).map(g => ({ value: g.id, label: g.ad }));
    return bosSecenekliOptions(list, data, 'gider_tipi_id');
  }, [data, giderTipleri]);

  const filteredData = useMemo(() => {
    return data.filter(d => {
      if (aramaMetni) {
        const q = aramaMetni.toLowerCase();
        if (!((d.cari || '').toLowerCase().includes(q) || (d.aciklama || '').toLowerCase().includes(q) || (d.fatura_no || '').toLowerCase().includes(q))) return false;
      }
      if (!secimeUyuyor(filtreCariIds, d.cari_id)) return false;
      if (!secimeUyuyor(filtreGiderTipiIds, d.gider_tipi_id)) return false;
      if (filtreOdemeDurumu.length > 0 && !filtreOdemeDurumu.includes(d.odendi)) return false;
      if (filtreTutarOp && filtreTutarDeger != null) {
        const tutar = Number(d.toplam_tutar || 0);
        const deger = Number(filtreTutarDeger);
        if (filtreTutarOp === 'gt' && !(tutar > deger)) return false;
        if (filtreTutarOp === 'gte' && !(tutar >= deger)) return false;
        if (filtreTutarOp === 'lt' && !(tutar < deger)) return false;
        if (filtreTutarOp === 'lte' && !(tutar <= deger)) return false;
        if (filtreTutarOp === 'eq' && !(tutar === deger)) return false;
      }
      if (filtreTarihBaslangic && dayjs(d.tarih).isBefore(filtreTarihBaslangic, 'day')) return false;
      if (filtreTarihBitis && dayjs(d.tarih).isAfter(filtreTarihBitis, 'day')) return false;
      return true;
    });
  }, [data, aramaMetni, filtreCariIds, filtreGiderTipiIds, filtreOdemeDurumu, filtreTutarOp, filtreTutarDeger, filtreTarihBaslangic, filtreTarihBitis]);

  const toplamlar = useMemo(() => {
    const t = { adet: filteredData.length, tutar: 0, odenen: 0, kalan: 0, odenmis: 0, odenmemis: 0, kismi: 0 };
    filteredData.forEach(d => {
      const tutar = Number(d.toplam_tutar || 0);
      const odenen = Number(d.odenen_tutar || 0);
      t.tutar += tutar;
      t.odenen += odenen;
      t.kalan += tutar - odenen;
      if (d.odendi === 1 || d.odendi === 3) t.odenmis += tutar;
      else if (d.odendi === 2) t.kismi += tutar;
      else t.odenmemis += tutar;
    });
    return t;
  }, [filteredData]);

  const filtreAktifMi = filtreCariIds.length > 0 || filtreGiderTipiIds.length > 0 || filtreOdemeDurumu.length > 0 || filtreTutarOp || filtreTarihBaslangic || filtreTarihBitis;

  const filtreTemizle = () => {
    setFiltreCariIds([]); setFiltreGiderTipiIds([]); setFiltreOdemeDurumu([]); setFiltreTutarOp(null); setFiltreTutarDeger(null); setFiltreTarihBaslangic(null); setFiltreTarihBitis(null);
  };

  const columns = [
    { title: 'Tarih', dataIndex: 'tarih', key: 'tarih', width: isMobile ? 65 : 110, render: (v) => v ? dayjs(v).format(isMobile ? 'DD.MM.YY' : 'DD.MM.YYYY') : '-', sorter: (a, b) => { const d = new Date(a.tarih || 0) - new Date(b.tarih || 0); return d !== 0 ? d : (a.id || 0) - (b.id || 0); }, defaultSortOrder: 'descend' },
    { title: 'Cari', dataIndex: 'cari', key: 'cari', sorter: (a, b) => (a.cari || '').localeCompare(b.cari || '', 'tr'), render: (v, r) => r.cari_id ? <a onClick={(e) => { e.stopPropagation(); navigate(`/cariler?detay=${r.cari_id}`); }}>{v}</a> : v },
    { title: 'Gider Tipi', dataIndex: 'gider_tipi', key: 'gider_tipi', width: 120, responsive: ['md'], sorter: (a, b) => (a.gider_tipi || '').localeCompare(b.gider_tipi || '', 'tr') },
    { title: 'Toplam', dataIndex: 'toplam_tutar', key: 'toplam_tutar', width: isMobile ? 90 : 120, align: 'right', render: (v) => v != null ? formatPara(v) : '-', sorter: (a, b) => (a.toplam_tutar || 0) - (b.toplam_tutar || 0) },
    { title: 'Ödeme', key: 'odendi', width: 140, align: 'center', responsive: ['sm'],
      render: (_, record) => {
        if (record.odendi === 3) return <Tag color="green" icon={<ThunderboltOutlined />}>Anlık Ödendi</Tag>;
        if (record.odendi === 1) return <Tag color="green" icon={<CheckCircleOutlined />}>Ödendi</Tag>;
        if (record.odendi === 2) return <Tag color="orange">Kısmi: {formatPara(record.odenen_tutar)}</Tag>;
        return <Tag color="red">Ödenmedi</Tag>;
      },
    },
    { title: 'Fatura No', dataIndex: 'fatura_no', key: 'fatura_no', responsive: ['md'] },
    { title: 'Açıklama', dataIndex: 'aciklama', key: 'aciklama', responsive: ['lg'] },
    {
      title: 'İşlem', key: 'islem', width: isMobile ? 130 : 220,
      render: (_, record) => (
        <Space size={4}>
          <Button size="small" icon={<EyeOutlined />} onClick={() => handleVadeAc(record)} title="Vade & Eşleşme" />
          <Button size="small" icon={<EditOutlined />} onClick={() => handleDuzenle(record.id)}>{!isMobile && 'Düzenle'}</Button>
          <Button size="small" icon={<FileTextOutlined />} onClick={() => handleDetay(record.id)}>{!isMobile && 'Detay'}</Button>
          <Popconfirm title="Bu faturayı silmek istediğinize emin misiniz? Stok hareketleri geri alınacak." onConfirm={() => handleDelete(record.id)} okText="Evet" cancelText="Hayır">
            <Button danger size="small" icon={<DeleteOutlined />}>{!isMobile && 'Sil'}</Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  const handleVadeAc = async (record) => {
    setVadeData({ kayit: record, vadeler: [] });
    setVadeModalOpen(true);
    try {
      const res = await alisVadeleriAPI.getByAlis(record.id);
      setVadeData({ kayit: record, vadeler: res.data || [] });
    } catch (err) {
      message.error('Vadeler yüklenemedi');
    }
  };

  const malzemeOptions = malzemeler.map(m => ({
    value: m.id,
    label: m.marka && m.marka !== 'markasız' ? `${m.ad} (${m.marka})` : m.ad,
  }));

  const handleMalzemeKaydet = async (values) => {
    try {
      await malzemelerAPI.create(values);
      message.success('Malzeme eklendi');
      setMalzemeModalOpen(false);
      malzemeForm.resetFields();
      const res = await malzemelerAPI.getAll();
      setMalzemeler(res.data);
    } catch (err) {
      message.error('Malzeme eklenemedi: ' + (err.response?.data?.hata || err.message));
    }
  };

  const malzemeNotFound = (
    <div style={{ textAlign: 'center', padding: 8 }}>
      <p style={{ color: '#999', marginBottom: 8 }}>
        "{malzemeArama}" bulunamadı
      </p>
      <Button
        type="link"
        onClick={() => {
          malzemeForm.setFieldsValue({ ad: malzemeArama });
          setMalzemeModalOpen(true);
        }}
      >
        Malzeme olarak eklemek için tıklayın
      </Button>
    </div>
  );

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginBottom: 16, flexWrap: 'nowrap' }}>
        <h2 style={{ margin: 0, fontSize: isMobile ? 16 : 24, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }}>Alışlar</h2>
        <Space size={6} style={{ flexShrink: 0 }}>
          <Input placeholder="Ara..." prefix={<SearchOutlined />} allowClear onChange={(e) => setAramaMetni(e.target.value)} style={{ width: isMobile ? 110 : 260 }} />
          <Button icon={<FilterOutlined />} onClick={() => setFiltreAcik(filtreAcik.length ? [] : ['1'])} type={filtreAktifMi ? 'primary' : 'default'} ghost={filtreAktifMi}
            title={filtreAktifMi ? 'Filtreler (Aktif)' : 'Filtreler'}>
            {!isMobile && `Filtreler${filtreAktifMi ? ' (Aktif)' : ''}`}
          </Button>
          {screens.lg && (
            <Button icon={<FileTextOutlined />} onClick={() => setFaturaModalOpen(true)}>Fatura/Fiş Oku</Button>
          )}
          <Button type="primary" icon={<PlusOutlined />} onClick={handleModalAc} title="Yeni Alış">
            {!isMobile && 'Yeni Alış'}
          </Button>
        </Space>
      </div>

      <Collapse activeKey={filtreAcik} onChange={setFiltreAcik} ghost items={[{ key: '1', showArrow: false, header: null, children: (
        <Card size="small" style={{ marginBottom: 16 }} title={<span><FilterOutlined /> Akıllı Filtreler</span>} extra={filtreAktifMi && <Button size="small" icon={<ClearOutlined />} onClick={filtreTemizle}>Temizle</Button>}>
          <Row gutter={[16, 12]}>
            <Col xs={24} sm={12} md={8}>
              <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>Cari / Tedarikçi</label>
              <Select mode="multiple" style={{ width: '100%' }} placeholder="Cari seçin..." options={cariFilterOptions} value={filtreCariIds} onChange={setFiltreCariIds} showSearch optionFilterProp="label" maxTagCount={2} allowClear />
            </Col>
            <Col xs={24} sm={12} md={8}>
              <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>Gider Tipi</label>
              <Select mode="multiple" style={{ width: '100%' }} placeholder="Gider tipi seçin..." options={giderTipiFilterOptions} value={filtreGiderTipiIds} onChange={setFiltreGiderTipiIds} showSearch optionFilterProp="label" maxTagCount={2} allowClear />
            </Col>
            <Col xs={24} sm={12} md={8}>
              <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>Ödeme Durumu</label>
              <Select mode="multiple" style={{ width: '100%' }} placeholder="Ödeme durumu seçin..." options={[{ value: 1, label: 'Ödendi' }, { value: 2, label: 'Kısmi Ödendi' }, { value: 0, label: 'Ödenmedi' }, { value: 3, label: 'Anlık Ödendi' }]} value={filtreOdemeDurumu} onChange={setFiltreOdemeDurumu} maxTagCount={2} allowClear />
            </Col>
            <Col xs={24} sm={12} md={8}>
              <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>Tutar Filtresi</label>
              <Space.Compact style={{ width: '100%' }}>
                <Select style={{ width: '45%' }} placeholder="Koşul" value={filtreTutarOp} onChange={setFiltreTutarOp} allowClear options={[{ value: 'gt', label: '>' }, { value: 'gte', label: '>=' }, { value: 'lt', label: '<' }, { value: 'lte', label: '<=' }, { value: 'eq', label: '=' }]} />
                <InputNumber {...paraInputProps} style={{ width: '55%' }} placeholder="Tutar" value={filtreTutarDeger} onChange={setFiltreTutarDeger} min={0} />
              </Space.Compact>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>Tarih Başlangıç</label>
              <DatePicker style={{ width: '100%' }} placeholder="Başlangıç" value={filtreTarihBaslangic} onChange={setFiltreTarihBaslangic} format="DD.MM.YYYY" />
            </Col>
            <Col xs={24} sm={12} md={8}>
              <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>Tarih Bitiş</label>
              <DatePicker style={{ width: '100%' }} placeholder="Bitiş" value={filtreTarihBitis} onChange={setFiltreTarihBitis} format="DD.MM.YYYY" />
            </Col>
          </Row>
        </Card>
      )}]} />

      {(
        <Card size="small" style={{ marginBottom: 16, background: '#fafafa' }}>
          <Row gutter={16}>
            <Col xs={12} sm={8} md={6}><Statistic title="Kayıt Sayısı" value={toplamlar.adet} /></Col>
            <Col xs={12} sm={8} md={6}><Statistic title="Toplam Tutar" value={toplamlar.tutar} precision={2} suffix="₺" /></Col>
            <Col xs={12} sm={8} md={6}><Statistic title="Ödenen" value={toplamlar.odenen} precision={2} suffix="₺" valueStyle={{ color: '#3f8600' }} /></Col>
            <Col xs={12} sm={8} md={6}><Statistic title="Kalan" value={toplamlar.kalan} precision={2} suffix="₺" valueStyle={{ color: '#cf1322' }} /></Col>
          </Row>
        </Card>
      )}

      <Table columns={columns} dataSource={filteredData} rowKey="id" loading={loading} pagination={{ defaultPageSize: 10, pageSizeOptions: ['10', '20', '50'], showSizeChanger: true, showTotal: (total, range) => `${range[0]}-${range[1]} / ${total} kayıt` }} />

      {/* Yeni Alış Modalı */}
      <Modal
        title="Yeni Alış"
        open={modalOpen}
        onCancel={() => { setModalOpen(false); setKalemler([]); form.resetFields(); }}
        onOk={() => form.submit()}
        width={isMobile ? '95vw' : 900}
        okText="Kaydet"
        style={isMobile ? { top: 10, paddingBottom: 0 } : undefined}
        styles={isMobile ? { body: { maxHeight: 'calc(100vh - 120px)', overflowY: 'auto', padding: 12 } } : undefined}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0 16px' }}>
            <Form.Item name="cari_id" label="Cari">
              <Select allowClear showSearch optionFilterProp="label" placeholder="Cari seçin" options={cariler.map(c => ({ value: c.id, label: c.ad }))} />
            </Form.Item>
            <Form.Item name="tarih" label="Tarih" rules={[{ required: true }]} initialValue={dayjs()}>
              <DatePicker format="DD.MM.YYYY" style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="fatura_no" label="Fatura No">
              <Input />
            </Form.Item>
          </div>
          <Form.Item name="aciklama" label="Açıklama">
            <Input />
          </Form.Item>
        </Form>

        <Divider orientation="left">Kalemler</Divider>

        {kalemler.map((kalem, idx) => (
          <div key={kalem.key} style={{ background: '#fafafa', borderRadius: 8, padding: 12, marginBottom: 8, border: '1px solid #f0f0f0' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <strong>Kalem {idx + 1}</strong>
              {kalemler.length > 1 && (
                <Button size="small" danger icon={<DeleteOutlined />} onClick={() => kalemSil(kalem.key)} />
              )}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto 80px 120px 120px', gap: '0 12px' }}>
              <div>
                <label style={{ fontSize: 12, color: '#666' }}>Gider Tipi *</label>
                <Select
                  style={{ width: '100%' }}
                  value={kalem.gider_tipi_id}
                  onChange={(v) => kalemGuncelle(kalem.key, 'gider_tipi_id', v)}
                  placeholder="Seçin"
                  showSearch
                  optionFilterProp="label"
                  options={giderTipleri.map(g => ({ value: g.id, label: g.ad }))}
                />
              </div>
              <div>
                <label style={{ fontSize: 12, color: '#666' }}>Açıklama</label>
                <Input
                  value={kalem.aciklama}
                  onChange={(e) => kalemGuncelle(kalem.key, 'aciklama', e.target.value)}
                  placeholder="Kalem açıklaması"
                />
              </div>
              <div>
                <label style={{ fontSize: 12, color: '#666', display: 'block' }}>Stok</label>
                <Switch
                  size="small"
                  checked={kalem.stoklu}
                  onChange={(v) => kalemGuncelle(kalem.key, 'stoklu', v)}
                  checkedChildren="Evet"
                  unCheckedChildren="Hayır"
                />
              </div>
              <div>
                <label style={{ fontSize: 12, color: '#666' }}>KDV</label>
                <Select
                  style={{ width: '100%' }}
                  value={kalem.kdv_orani}
                  onChange={(v) => kalemGuncelle(kalem.key, 'kdv_orani', v)}
                  options={kdvOranlari}
                />
              </div>
              <div>
                <label style={{ fontSize: 12, color: '#666' }}>KDV Hariç</label>
                {kalem.stoklu ? (
                  <InputNumber
                    style={{ width: '100%' }}
                    value={kalemKdvHaricTutar(kalem)}
                    disabled
                    formatter={(v) => formatPara(v || 0)}
                  />
                ) : (
                  <InputNumber
                    {...paraInputProps}
                    style={{ width: '100%' }}
                    value={kalem.tutarHaric}
                    onChange={(v) => kalemGuncelle(kalem.key, 'tutarHaric', v)}
                  />
                )}
              </div>
              <div>
                <label style={{ fontSize: 12, color: '#666' }}>KDV Dahil {!kalem.stoklu && '*'}</label>
                {kalem.stoklu ? (
                  <InputNumber
                    style={{ width: '100%' }}
                    value={kalemKdvDahilTutar(kalem)}
                    disabled
                    formatter={(v) => formatPara(v || 0)}
                  />
                ) : (
                  <InputNumber
                    {...paraInputProps}
                    style={{ width: '100%' }}
                    value={kalem.tutar}
                    onChange={(v) => kalemGuncelle(kalem.key, 'tutar', v)}
                  />
                )}
              </div>
            </div>

            {kalem.stoklu && (
              <div style={{ marginTop: 12 }}>
                {kalem.malzemeSatirlari.map((satir, sIdx) => (
                  <div key={satir.key} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr 1fr 32px', gap: '0 8px', marginBottom: 6, alignItems: 'end' }}>
                    <div>
                      {sIdx === 0 && <label style={{ fontSize: 12, color: '#666' }}>Malzeme *</label>}
                      <Select
                        style={{ width: '100%' }}
                        value={satir.malzeme_id}
                        onChange={(v) => malzemeSatiriGuncelle(kalem.key, satir.key, 'malzeme_id', v)}
                        placeholder="Malzeme seçin"
                        showSearch
                        optionFilterProp="label"
                        options={malzemeOptions}
                        onSearch={(val) => setMalzemeArama(val)}
                        notFoundContent={malzemeArama ? malzemeNotFound : null}
                      />
                    </div>
                    <div>
                      {sIdx === 0 && <label style={{ fontSize: 12, color: '#666' }}>Miktar *</label>}
                      <InputNumber
                        style={{ width: '100%' }}
                        min={0}
                        step={0.001}
                        precision={3}
                        value={satir.miktar}
                        onChange={(v) => malzemeSatiriGuncelle(kalem.key, satir.key, 'miktar', v)}
                      />
                    </div>
                    <div>
                      {sIdx === 0 && <label style={{ fontSize: 12, color: '#666' }}>Birim *</label>}
                      <Space.Compact style={{ width: '100%' }}>
                        <Select
                          style={{ width: '100%' }}
                          value={satir.birim_id}
                          onChange={(v) => malzemeSatiriGuncelle(kalem.key, satir.key, 'birim_id', v)}
                          options={getSecilebilenBirimler(malzemeler, satir.malzeme_id)}
                        />
                        <Button icon={<SwapOutlined />} title="Birim dönüşümü ekle" disabled={!satir.malzeme_id}
                          onClick={() => { const m = malzemeler.find(mm => mm.id === satir.malzeme_id); if (m) { setDonusumMalzeme(m); setDonusumOpen(true); } }} />
                      </Space.Compact>
                    </div>
                    <div>
                      {sIdx === 0 && <label style={{ fontSize: 12, color: '#666' }}>KDV Hariç B.Fiyat *</label>}
                      <InputNumber
                        {...paraInputProps}
                        style={{ width: '100%' }}
                        min={0}
                        value={satir.birim_fiyat}
                        onChange={(v) => malzemeSatiriGuncelle(kalem.key, satir.key, 'birim_fiyat', v)}
                      />
                    </div>
                    <div>
                      {sIdx === 0 && <label style={{ fontSize: 12, color: '#666' }}>KDV Dahil B.Fiyat *</label>}
                      <InputNumber
                        {...paraInputProps}
                        style={{ width: '100%' }}
                        min={0}
                        value={satir.birim_fiyat_dahil}
                        onChange={(v) => malzemeSatiriGuncelle(kalem.key, satir.key, 'birim_fiyat_dahil', v)}
                      />
                    </div>
                    <div>
                      {sIdx === 0 && <label style={{ fontSize: 12, color: '#666' }}>KDV Hariç Tutar</label>}
                      <InputNumber
                        style={{ width: '100%' }}
                        value={satir.tutar}
                        disabled
                        formatter={(v) => formatPara(v || 0)}
                      />
                    </div>
                    <div>
                      {sIdx === 0 && <label style={{ fontSize: 12, color: '#666' }}>&nbsp;</label>}
                      {kalem.malzemeSatirlari.length > 1 && (
                        <Button size="small" danger icon={<DeleteOutlined />} onClick={() => malzemeSatiriSil(kalem.key, satir.key)} />
                      )}
                    </div>
                  </div>
                ))}
                <Button type="dashed" size="small" icon={<PlusOutlined />} onClick={() => malzemeSatiriEkle(kalem.key)} style={{ marginTop: 4 }}>
                  Malzeme Ekle
                </Button>
              </div>
            )}
          </div>
        ))}

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12 }}>
          <Button type="dashed" icon={<PlusOutlined />} onClick={kalemEkle}>Kalem Ekle</Button>
          <div style={{ textAlign: 'right', lineHeight: '1.8' }}>
            <div style={{ fontSize: 13, color: '#666' }}>Ara Toplam: {formatPara(kalemlerKdvHaric)}</div>
            <div style={{ fontSize: 13, color: '#666' }}>KDV: {formatPara(kalemlerKdv)}</div>
            <strong>KDV Dahil Toplam: {formatPara(kalemlerToplam)}</strong>
          </div>
        </div>

        <Divider orientation="left">Ödeme</Divider>
        <div style={{
          background: hemenOdendi ? '#f6ffed' : '#fafafa',
          border: `1px solid ${hemenOdendi ? '#b7eb8f' : '#f0f0f0'}`,
          borderRadius: 8, padding: 12
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: hemenOdendi ? 12 : 0 }}>
            <Switch checked={hemenOdendi} onChange={setHemenOdendi} />
            <span style={{ fontWeight: 600 }}>Hemen ödendi</span>
            {hemenOdendi && <span style={{ fontSize: 12, color: '#52c41a' }}>Ödeme otomatik oluşturulacak ve kasa bakiyesi güncellenecek</span>}
          </div>
          {hemenOdendi && (
            <div>
              <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>Kasa *</label>
              <Select
                style={{ width: '100%' }}
                showSearch
                optionFilterProp="label"
                placeholder="Ödemenin çıkacağı kasayı seçin"
                options={kasalar.map(k => ({ value: k.id, label: k.ad }))}
                value={seciliKasa}
                onChange={setSeciliKasa}
              />
            </div>
          )}
        </div>
      </Modal>

      {/* Düzenleme Modalı */}
      <Modal
        title={duzenleData ? `Alış #${duzenleData.id} - Düzenle` : 'Alış Düzenle'}
        open={duzenleModalOpen}
        onCancel={() => { setDuzenleModalOpen(false); setDuzenleKalemler([]); duzenleForm.resetFields(); }}
        onOk={() => duzenleForm.submit()}
        width={900}
        okText="Güncelle"
      >
        <Form form={duzenleForm} layout="vertical" onFinish={handleDuzenleSubmit}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0 16px' }}>
            <Form.Item name="cari_id" label="Cari">
              <Select allowClear showSearch optionFilterProp="label" placeholder="Cari seçin" options={cariler.map(c => ({ value: c.id, label: c.ad }))} />
            </Form.Item>
            <Form.Item name="tarih" label="Tarih" rules={[{ required: true }]}>
              <DatePicker format="DD.MM.YYYY" style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="fatura_no" label="Fatura No">
              <Input />
            </Form.Item>
          </div>
          <Form.Item name="aciklama" label="Açıklama">
            <Input />
          </Form.Item>
        </Form>

        <Divider orientation="left">Kalemler</Divider>

        {duzenleKalemler.map((kalem, idx) => (
          <div key={kalem.key} style={{ background: '#fafafa', borderRadius: 8, padding: 12, marginBottom: 8, border: '1px solid #f0f0f0' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <strong>Kalem {idx + 1}</strong>
              {duzenleKalemler.length > 1 && (
                <Button size="small" danger icon={<DeleteOutlined />} onClick={() => duzenleKalemSil(kalem.key)} />
              )}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto 80px 120px 120px', gap: '0 12px' }}>
              <div>
                <label style={{ fontSize: 12, color: '#666' }}>Gider Tipi *</label>
                <Select
                  style={{ width: '100%' }}
                  value={kalem.gider_tipi_id}
                  onChange={(v) => duzenleKalemGuncelle(kalem.key, 'gider_tipi_id', v)}
                  placeholder="Seçin"
                  showSearch
                  optionFilterProp="label"
                  options={giderTipleri.map(g => ({ value: g.id, label: g.ad }))}
                />
              </div>
              <div>
                <label style={{ fontSize: 12, color: '#666' }}>Açıklama</label>
                <Input
                  value={kalem.aciklama}
                  onChange={(e) => duzenleKalemGuncelle(kalem.key, 'aciklama', e.target.value)}
                  placeholder="Kalem açıklaması"
                />
              </div>
              <div>
                <label style={{ fontSize: 12, color: '#666', display: 'block' }}>Stok</label>
                <Switch
                  size="small"
                  checked={kalem.stoklu}
                  onChange={(v) => duzenleKalemGuncelle(kalem.key, 'stoklu', v)}
                  checkedChildren="Evet"
                  unCheckedChildren="Hayır"
                />
              </div>
              <div>
                <label style={{ fontSize: 12, color: '#666' }}>KDV</label>
                <Select
                  style={{ width: '100%' }}
                  value={kalem.kdv_orani}
                  onChange={(v) => duzenleKalemGuncelle(kalem.key, 'kdv_orani', v)}
                  options={kdvOranlari}
                />
              </div>
              <div>
                <label style={{ fontSize: 12, color: '#666' }}>KDV Hariç</label>
                {kalem.stoklu ? (
                  <InputNumber
                    style={{ width: '100%' }}
                    value={kalemKdvHaricTutar(kalem)}
                    disabled
                    formatter={(v) => formatPara(v || 0)}
                  />
                ) : (
                  <InputNumber
                    {...paraInputProps}
                    style={{ width: '100%' }}
                    value={kalem.tutarHaric}
                    onChange={(v) => duzenleKalemGuncelle(kalem.key, 'tutarHaric', v)}
                  />
                )}
              </div>
              <div>
                <label style={{ fontSize: 12, color: '#666' }}>KDV Dahil {!kalem.stoklu && '*'}</label>
                {kalem.stoklu ? (
                  <InputNumber
                    style={{ width: '100%' }}
                    value={kalemKdvDahilTutar(kalem)}
                    disabled
                    formatter={(v) => formatPara(v || 0)}
                  />
                ) : (
                  <InputNumber
                    {...paraInputProps}
                    style={{ width: '100%' }}
                    value={kalem.tutar}
                    onChange={(v) => duzenleKalemGuncelle(kalem.key, 'tutar', v)}
                  />
                )}
              </div>
            </div>

            {kalem.stoklu && (
              <div style={{ marginTop: 12 }}>
                {kalem.malzemeSatirlari.map((satir, sIdx) => (
                  <div key={satir.key} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr 1fr 32px', gap: '0 8px', marginBottom: 6, alignItems: 'end' }}>
                    <div>
                      {sIdx === 0 && <label style={{ fontSize: 12, color: '#666' }}>Malzeme *</label>}
                      <Select
                        style={{ width: '100%' }}
                        value={satir.malzeme_id}
                        onChange={(v) => duzenleMalzemeSatiriGuncelle(kalem.key, satir.key, 'malzeme_id', v)}
                        placeholder="Malzeme seçin"
                        showSearch
                        optionFilterProp="label"
                        options={malzemeOptions}
                        onSearch={(val) => setMalzemeArama(val)}
                        notFoundContent={malzemeArama ? malzemeNotFound : null}
                      />
                    </div>
                    <div>
                      {sIdx === 0 && <label style={{ fontSize: 12, color: '#666' }}>Miktar *</label>}
                      <InputNumber
                        style={{ width: '100%' }}
                        min={0}
                        step={0.001}
                        precision={3}
                        value={satir.miktar}
                        onChange={(v) => duzenleMalzemeSatiriGuncelle(kalem.key, satir.key, 'miktar', v)}
                      />
                    </div>
                    <div>
                      {sIdx === 0 && <label style={{ fontSize: 12, color: '#666' }}>Birim *</label>}
                      <Space.Compact style={{ width: '100%' }}>
                        <Select
                          style={{ width: '100%' }}
                          value={satir.birim_id}
                          onChange={(v) => duzenleMalzemeSatiriGuncelle(kalem.key, satir.key, 'birim_id', v)}
                          options={getSecilebilenBirimler(malzemeler, satir.malzeme_id)}
                        />
                        <Button icon={<SwapOutlined />} title="Birim dönüşümü ekle" disabled={!satir.malzeme_id}
                          onClick={() => { const m = malzemeler.find(mm => mm.id === satir.malzeme_id); if (m) { setDonusumMalzeme(m); setDonusumOpen(true); } }} />
                      </Space.Compact>
                    </div>
                    <div>
                      {sIdx === 0 && <label style={{ fontSize: 12, color: '#666' }}>KDV Hariç B.Fiyat *</label>}
                      <InputNumber
                        {...paraInputProps}
                        style={{ width: '100%' }}
                        min={0}
                        value={satir.birim_fiyat}
                        onChange={(v) => duzenleMalzemeSatiriGuncelle(kalem.key, satir.key, 'birim_fiyat', v)}
                      />
                    </div>
                    <div>
                      {sIdx === 0 && <label style={{ fontSize: 12, color: '#666' }}>KDV Dahil B.Fiyat *</label>}
                      <InputNumber
                        {...paraInputProps}
                        style={{ width: '100%' }}
                        min={0}
                        value={satir.birim_fiyat_dahil}
                        onChange={(v) => duzenleMalzemeSatiriGuncelle(kalem.key, satir.key, 'birim_fiyat_dahil', v)}
                      />
                    </div>
                    <div>
                      {sIdx === 0 && <label style={{ fontSize: 12, color: '#666' }}>KDV Hariç Tutar</label>}
                      <InputNumber
                        style={{ width: '100%' }}
                        value={satir.tutar}
                        disabled
                        formatter={(v) => formatPara(v || 0)}
                      />
                    </div>
                    <div>
                      {sIdx === 0 && <label style={{ fontSize: 12, color: '#666' }}>&nbsp;</label>}
                      {kalem.malzemeSatirlari.length > 1 && (
                        <Button size="small" danger icon={<DeleteOutlined />} onClick={() => duzenleMalzemeSatiriSil(kalem.key, satir.key)} />
                      )}
                    </div>
                  </div>
                ))}
                <Button type="dashed" size="small" icon={<PlusOutlined />} onClick={() => duzenleMalzemeSatiriEkle(kalem.key)} style={{ marginTop: 4 }}>
                  Malzeme Ekle
                </Button>
              </div>
            )}
          </div>
        ))}

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12 }}>
          <Button type="dashed" icon={<PlusOutlined />} onClick={duzenleKalemEkle}>Kalem Ekle</Button>
          <div style={{ textAlign: 'right', lineHeight: '1.8' }}>
            <div style={{ fontSize: 13, color: '#666' }}>Ara Toplam: {formatPara(duzenleKalemlerKdvHaric)}</div>
            <div style={{ fontSize: 13, color: '#666' }}>KDV: {formatPara(duzenleKalemlerKdv)}</div>
            <strong>KDV Dahil Toplam: {formatPara(duzenleKalemlerToplam)}</strong>
          </div>
        </div>
      </Modal>

      {/* Detay Modalı */}
      <Modal
        title={detayData ? `Alış #${detayData.id}` : 'Detay'}
        open={detayModalOpen}
        onCancel={() => { setDetayModalOpen(false); setDetayData(null); }}
        footer={<Button onClick={() => { setDetayModalOpen(false); setDetayData(null); }}>Kapat</Button>}
        width={700}
      >
        {detayData && (
          <div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 16 }}>
              <div><strong>Cari:</strong> {detayData.cari || '-'}</div>
              <div><strong>Tarih:</strong> {detayData.tarih ? dayjs(detayData.tarih).format('DD.MM.YYYY') : '-'}</div>
              <div><strong>Fatura No:</strong> {detayData.fatura_no || '-'}</div>
              <div><strong>Toplam:</strong> {formatPara(detayData.toplam_tutar)}</div>
              {detayData.aciklama && <div style={{ gridColumn: '1 / -1' }}><strong>Açıklama:</strong> {detayData.aciklama}</div>}
              {detayData.gorsel_path && (() => {
                const dosya = String(detayData.gorsel_path).split('/').pop();
                const url = `${API_BASE_URL}/fatura-gorseller/${dosya}`;
                const isGorsel = /\.(jpe?g|png|gif|webp)$/i.test(dosya);
                const isXml = /\.xml$/i.test(dosya);
                return (
                  <div style={{ gridColumn: '1 / -1', marginTop: 8 }}>
                    <strong>{isXml ? 'Fatura XML:' : 'Fatura Görseli:'}</strong>{' '}
                    <a href={url} target="_blank" rel="noopener noreferrer">Yeni sekmede aç</a>
                    {isGorsel && (
                      <div style={{ marginTop: 8 }}>
                        <Image src={url} alt="Fatura" style={{ maxWidth: '100%', maxHeight: 300 }} />
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
            <Table
              dataSource={detayData.kalemler}
              rowKey="id"
              size="small"
              pagination={false}
              columns={[
                { title: 'Gider Tipi', dataIndex: 'gider_tipi', key: 'gider_tipi' },
                { title: 'Açıklama', dataIndex: 'aciklama', key: 'aciklama' },
                { title: 'Tutar', dataIndex: 'tutar', key: 'tutar', width: 100, align: 'right', render: (v) => formatPara(v) },
                { title: 'Stok', key: 'stok', width: 60, align: 'center', render: (_, r) => r.stok_takibi ? <Tag color="green">Evet</Tag> : <Tag>Hayır</Tag> },
                { title: 'Malzeme', dataIndex: 'malzeme', key: 'malzeme', render: (v) => v || '-' },
                { title: 'Miktar', key: 'miktar', width: 100, render: (_, r) => r.stok_takibi ? `${r.miktar} ${r.birim || ''}` : '-' },
              ]}
            />
          </div>
        )}
      </Modal>
      {/* Hızlı Malzeme Ekleme Modalı */}
      <Modal
        title="Hızlı Malzeme Ekle"
        open={malzemeModalOpen}
        onCancel={() => { setMalzemeModalOpen(false); malzemeForm.resetFields(); }}
        onOk={() => malzemeForm.submit()}
        zIndex={1100}
      >
        <Form form={malzemeForm} layout="vertical" onFinish={handleMalzemeKaydet}>
          <Form.Item name="ad" label="Ad" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="marka" label="Marka">
            <Input />
          </Form.Item>
          <Form.Item name="aciklama" label="Açıklama">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item name="malzeme_tipi_id" label="Tip">
            <Select options={malzemeTipleri} />
          </Form.Item>
          <Form.Item name="birim_id" label="Birim">
            <Select options={birimler} />
          </Form.Item>
          <Form.Item name="kritik_seviye" label="Kritik Seviye">
            <InputNumber style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>
      {/* Fatura Okuma Modalı */}
      <Modal
        title="Fatura / Fiş Oku"
        open={faturaModalOpen}
        onCancel={faturaModalKapat}
        width={800}
        footer={[
          <Button key="kapat" onClick={faturaModalKapat}>Kapat</Button>,
          faturaResults.filter(r => r.status === 'done' && r.result?.kalemler?.length > 0).length > 0 && (
            <Button key="kaydet" type="primary" loading={faturaKaydetLoading} onClick={handleFaturaTumunuKaydet}>
              Tümünü Kaydet ({faturaResults.filter(r => r.status === 'done').length} fatura)
            </Button>
          )
        ]}
      >
        <Upload.Dragger
          accept=".pdf,.jpg,.jpeg,.png,.gif,.webp,.xml,.zip"
          multiple
          beforeUpload={handleFaturaYukle}
          showUploadList={false}
          style={{ marginBottom: faturaResults.length > 0 ? 16 : 0 }}
        >
          <p style={{ fontSize: 32, margin: 0 }}><UploadOutlined /></p>
          <p style={{ fontSize: 14, margin: '8px 0 0' }}>Fatura veya fiş dosyalarını sürükleyin veya tıklayın</p>
          <p style={{ color: '#999', fontSize: 12, margin: 0 }}>PDF, JPG, PNG, e-Fatura XML ve toplu XML içeren ZIP desteklenir (maks. 50MB) - Birden fazla dosya seçebilirsiniz</p>
          <p style={{ color: '#999', fontSize: 12, margin: 0 }}>XML/ZIP dosyaları doğrudan okunur: tutar ve KDV bilgileri faturadan birebir alınır</p>
        </Upload.Dragger>

        {faturaResults.length > 0 && (
          <Collapse accordion style={{ marginTop: 16 }}>
            {faturaResults.map((entry) => {
              const statusIcon = entry.status === 'uploading' ? <Spin size="small" />
                : entry.status === 'done' ? <CheckCircleOutlined style={{ color: '#52c41a' }} />
                : entry.status === 'saved' ? <CheckCircleOutlined style={{ color: '#1890ff' }} />
                : entry.status === 'saving' ? <Spin size="small" />
                : <span style={{ color: '#ff4d4f' }}>✕</span>;

              const statusText = entry.status === 'uploading' ? 'Okunuyor...'
                : entry.status === 'done' ? `${entry.result?.kalemler?.length || 0} kalem`
                : entry.status === 'saved' ? 'Kaydedildi'
                : entry.status === 'saving' ? 'Kaydediliyor...'
                : entry.error || 'Hata';

              const tedarikci = entry.result?.tedarikci;

              return (
                <Collapse.Panel
                  key={entry.id}
                  header={
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      {statusIcon}
                      <span style={{ fontWeight: 500 }}>{entry.fileName}</span>
                      {tedarikci && <Tag>{tedarikci}</Tag>}
                      {entry.eslesme && entry.status === 'done' && <Tag color="warning">Benzer kayıt var</Tag>}
                      <span style={{ color: '#999', fontSize: 12 }}>{statusText}</span>
                    </div>
                  }
                  extra={
                    <Space onClick={e => e.stopPropagation()}>
                      {entry.status === 'done' && (
                        <Button size="small" type="primary" onClick={() => handleFaturaKaydetTek(entry.id)}>Kaydet</Button>
                      )}
                      {entry.status !== 'saving' && entry.status !== 'saved' && (
                        <Button size="small" danger onClick={() => handleFaturaSil(entry.id)}>Sil</Button>
                      )}
                    </Space>
                  }
                >
                  {entry.status === 'done' && entry.result && (
                    <div>
                      {entry.eslesme && (
                        <Card size="small" style={{ marginBottom: 12, background: '#fffbe6', borderColor: '#ffe58f' }}>
                          <div style={{ marginBottom: 8 }}>
                            <Tag color="warning" style={{ fontSize: 13 }}>Benzer kayıt bulundu</Tag>
                            <span style={{ fontSize: 13 }}>
                              <strong>{entry.eslesme.cari}</strong> - {dayjs(entry.eslesme.tarih).format('DD.MM.YYYY')} - {formatPara(entry.eslesme.toplam_tutar)}
                            </span>
                          </div>
                          <Select
                            size="small"
                            value={entry.guncellemeSec}
                            onChange={(v) => setFaturaResults(prev => prev.map(r => r.id === entry.id ? { ...r, guncellemeSec: v } : r))}
                            style={{ width: '100%' }}
                            options={[
                              { value: 'guncelle', label: 'Mevcut kaydı güncelle (eski kalemler silinip yeni kalemlerle değiştirilecek)' },
                              { value: 'yeni', label: 'Yeni kayıt oluştur (mevcut kayda dokunma)' },
                            ]}
                          />
                        </Card>
                      )}
                      <Row gutter={[12, 8]} style={{ marginBottom: 12 }}>
                        <Col span={12}>
                          <label style={{ fontSize: 12, color: '#666' }}>Tedarikçi</label>
                          <Input
                            size="small"
                            value={entry.result.tedarikci || ''}
                            onChange={(e) => faturaResultGuncelle(entry.id, 'tedarikci', e.target.value)}
                            suffix={
                              cariEslestir(entry.result.tedarikci)
                                ? <Tag color="green" style={{ margin: 0 }}>Eşleşti</Tag>
                                : entry.result.tedarikci ? <Tag color="orange" style={{ margin: 0, cursor: 'pointer' }}
                                    onClick={() => faturaHizliCariAc(entry.result.tedarikci, (yeniAd) => {
                                      faturaResultGuncelle(entry.id, 'tedarikci', yeniAd);
                                    })}>Yeni - Tıkla</Tag> : null
                            }
                          />
                          {(() => {
                            const eslesme = cariEslestir(entry.result.tedarikci);
                            const benzerler = benzerCariBul(entry.result.tedarikci);
                            if (eslesme) return null;
                            const filtreliBenzerler = benzerler.filter(c => normalizeAd(c.ad) !== normalizeAd(entry.result.tedarikci));
                            return (
                              <div style={{ marginTop: 4 }}>
                                {filtreliBenzerler.length > 0 && filtreliBenzerler.map(c => (
                                  <Tag key={c.id} style={{ cursor: 'pointer', marginBottom: 2 }} color={c.aktif ? 'processing' : 'default'}
                                    onClick={() => faturaResultGuncelle(entry.id, 'tedarikci', c.ad)}>
                                    {c.ad} {!c.aktif && '(pasif)'}
                                  </Tag>
                                ))}
                                {filtreliBenzerler.length === 0 && entry.result.tedarikci && (
                                  <Tag color="blue" style={{ cursor: 'pointer', marginBottom: 2 }}
                                    onClick={() => faturaHizliCariAc(entry.result.tedarikci, (yeniAd) => {
                                      faturaResultGuncelle(entry.id, 'tedarikci', yeniAd);
                                    })}>
                                    "{entry.result.tedarikci}" cari olarak oluştur
                                  </Tag>
                                )}
                              </div>
                            );
                          })()}
                        </Col>
                        <Col span={6}>
                          <label style={{ fontSize: 12, color: '#666' }}>Fatura No</label>
                          <Input size="small" value={entry.result.fatura_no || ''} onChange={(e) => faturaResultGuncelle(entry.id, 'fatura_no', e.target.value)} />
                        </Col>
                        <Col span={6}>
                          <label style={{ fontSize: 12, color: '#666' }}>Tarih</label>
                          <DatePicker size="small" style={{ width: '100%' }} value={entry.result.tarih ? dayjs(entry.result.tarih) : null}
                            onChange={(d) => faturaResultGuncelle(entry.id, 'tarih', d ? d.format('YYYY-MM-DD') : null)} format="DD.MM.YYYY" />
                        </Col>
                      </Row>

                      <Divider orientation="left" style={{ margin: '8px 0' }}>Kalemler</Divider>

                      {entry.kalemler.map((kalem, kIdx) => (
                        <div key={kalem.key} style={{ background: '#f9f9f9', borderRadius: 8, padding: 12, marginBottom: 8, border: '1px solid #f0f0f0' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                            <strong>Kalem {kIdx + 1}</strong>
                            {entry.kalemler.length > 1 && (
                              <Button size="small" danger icon={<DeleteOutlined />} onClick={() => faturaKalemSil(entry.id, kalem.key)} />
                            )}
                          </div>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto 80px 120px', gap: '0 12px' }}>
                            <div>
                              <label style={{ fontSize: 12, color: '#666' }}>Gider Tipi *</label>
                              <Select style={{ width: '100%' }} value={kalem.gider_tipi_id}
                                onChange={(v) => faturaKalemGuncelle(entry.id, kalem.key, 'gider_tipi_id', v)}
                                placeholder="Seçin" showSearch optionFilterProp="label"
                                options={giderTipleri.map(g => ({ value: g.id, label: g.ad }))} />
                            </div>
                            <div>
                              <label style={{ fontSize: 12, color: '#666' }}>Açıklama</label>
                              <Input value={kalem.aciklama} onChange={(e) => faturaKalemGuncelle(entry.id, kalem.key, 'aciklama', e.target.value)} />
                            </div>
                            <div>
                              <label style={{ fontSize: 12, color: '#666', display: 'block' }}>Stok</label>
                              <Switch size="small" checked={kalem.stoklu}
                                onChange={(v) => faturaKalemGuncelle(entry.id, kalem.key, 'stoklu', v)}
                                checkedChildren="Evet" unCheckedChildren="Hayır"
                                disabled={kalem.kdv_orani === null} />
                            </div>
                            <div>
                              <label style={{ fontSize: 12, color: '#666' }}>KDV</label>
                              {kalem.kdv_orani === null ? (
                                <Input style={{ width: '100%' }} value="-" disabled />
                              ) : (
                                <Select style={{ width: '100%' }} value={kalem.kdv_orani}
                                  onChange={(v) => faturaKalemGuncelle(entry.id, kalem.key, 'kdv_orani', v)}
                                  options={kdvOranlari} />
                              )}
                            </div>
                            <div>
                              {kalem.kdv_orani === null ? (
                                <>
                                  <label style={{ fontSize: 12, color: '#666' }}>KDV Hariç / KDV</label>
                                  <div style={{ display: 'flex', gap: 4 }}>
                                    <InputNumber {...paraInputProps} style={{ width: '50%' }} value={kalem.tutar}
                                      onChange={(v) => faturaKalemGuncelle(entry.id, kalem.key, 'tutar', v)} />
                                    <InputNumber {...paraInputProps} style={{ width: '50%' }} value={kalem.kusurat_kdv_tutar}
                                      onChange={(v) => faturaKalemGuncelle(entry.id, kalem.key, 'kusurat_kdv_tutar', v)} />
                                  </div>
                                </>
                              ) : (
                                <>
                                  <label style={{ fontSize: 12, color: '#666' }}>KDV Dahil Tutar</label>
                                  {kalem.stoklu ? (
                                    <InputNumber style={{ width: '100%' }} value={(() => {
                                      const kdvHaric = kalem.malzemeSatirlari.reduce((t, s) => t + ((s.miktar || 0) * (s.birim_fiyat || 0)), 0);
                                      return Number((kdvHaric * (1 + (kalem.kdv_orani || 0) / 100)).toFixed(2));
                                    })()} disabled formatter={(v) => formatPara(v || 0)} />
                                  ) : (
                                    <InputNumber {...paraInputProps} style={{ width: '100%' }} min={0} value={kalem.tutar}
                                      onChange={(v) => faturaKalemGuncelle(entry.id, kalem.key, 'tutar', v)} />
                                  )}
                                </>
                              )}
                            </div>
                          </div>

                          {kalem.stoklu && (
                            <div style={{ marginTop: 12 }}>
                              {kalem.malzemeSatirlari.map((satir, sIdx) => (
                                <div key={satir.key} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr 1fr 32px', gap: '0 8px', marginBottom: 6, alignItems: 'end' }}>
                                  <div>
                                    {sIdx === 0 && <label style={{ fontSize: 12, color: '#666' }}>Malzeme *</label>}
                                    <Select
                                      style={{ width: '100%' }}
                                      value={satir.malzeme_id}
                                      onChange={(v) => faturaMalzemeSatiriGuncelle(entry.id, kalem.key, satir.key, 'malzeme_id', v)}
                                      placeholder={satir.malzeme_ad_ocr || 'Malzeme seçin'}
                                      showSearch optionFilterProp="label"
                                      options={malzemeOptions}
                                      notFoundContent={
                                        <div style={{ textAlign: 'center', padding: 8 }}>
                                          <Button type="link" onClick={() => faturaHizliMalzemeAc(satir.malzeme_ad_ocr || '', satir.birim_ad_ocr, (yeniId) => {
                                            faturaMalzemeSatiriGuncelle(entry.id, kalem.key, satir.key, 'malzeme_id', yeniId);
                                          })}>Malzeme olarak eklemek için tıklayın</Button>
                                        </div>
                                      }
                                    />
                                    {!satir.malzeme_id && satir.malzeme_ad_ocr && (
                                      <Tag color="blue" style={{ cursor: 'pointer', marginTop: 2, fontSize: 11 }}
                                        onClick={() => faturaHizliMalzemeAc(satir.malzeme_ad_ocr, satir.birim_ad_ocr, (yeniId) => {
                                          faturaMalzemeSatiriGuncelle(entry.id, kalem.key, satir.key, 'malzeme_id', yeniId);
                                        })}>
                                        Yeni: "{satir.malzeme_ad_ocr}" - Tıkla ve oluştur
                                      </Tag>
                                    )}
                                    {satir.malzeme_id && (
                                      <Tag color="green" style={{ marginTop: 2, fontSize: 11 }}>Mevcut</Tag>
                                    )}
                                    {!satir.malzeme_id && satir.malzeme_ad_ocr && (() => {
                                      const benzerler = benzerMalzemeBul(satir.malzeme_ad_ocr);
                                      if (benzerler.length === 0) return null;
                                      return (
                                        <div style={{ marginTop: 2 }}>
                                          {benzerler.map(m => (
                                            <Tag key={m.id} style={{ cursor: 'pointer', marginBottom: 2, fontSize: 11 }} color="processing"
                                              onClick={() => faturaMalzemeSatiriGuncelle(entry.id, kalem.key, satir.key, 'malzeme_id', m.id)}>
                                              {m.ad}
                                            </Tag>
                                          ))}
                                        </div>
                                      );
                                    })()}
                                  </div>
                                  <div>
                                    {sIdx === 0 && <label style={{ fontSize: 12, color: '#666' }}>Miktar *</label>}
                                    <InputNumber style={{ width: '100%' }} min={0} step={0.001} precision={3} value={satir.miktar}
                                      onChange={(v) => faturaMalzemeSatiriGuncelle(entry.id, kalem.key, satir.key, 'miktar', v)} />
                                  </div>
                                  <div>
                                    {sIdx === 0 && <label style={{ fontSize: 12, color: '#666' }}>Birim *</label>}
                                    <Space.Compact style={{ width: '100%' }}>
                                      <Select style={{ width: '100%' }} value={satir.birim_id}
                                        onChange={(v) => faturaMalzemeSatiriGuncelle(entry.id, kalem.key, satir.key, 'birim_id', v)}
                                        options={getSecilebilenBirimler(malzemeler, satir.malzeme_id)} />
                                      <Button icon={<SwapOutlined />} title="Birim dönüşümü ekle" disabled={!satir.malzeme_id}
                                        onClick={() => { const m = malzemeler.find(mm => mm.id === satir.malzeme_id); if (m) { setDonusumMalzeme(m); setDonusumOpen(true); } }} />
                                    </Space.Compact>
                                  </div>
                                  <div>
                                    {sIdx === 0 && <label style={{ fontSize: 12, color: '#666' }}>KDV Hariç B.Fiyat *</label>}
                                    <InputNumber {...paraInputProps} style={{ width: '100%' }} min={0} value={satir.birim_fiyat}
                                      onChange={(v) => faturaMalzemeSatiriGuncelle(entry.id, kalem.key, satir.key, 'birim_fiyat', v)} />
                                  </div>
                                  <div>
                                    {sIdx === 0 && <label style={{ fontSize: 12, color: '#666' }}>KDV Hariç Tutar</label>}
                                    <InputNumber style={{ width: '100%' }} value={satir.tutar} disabled formatter={(v) => formatPara(v || 0)} />
                                  </div>
                                  <div>
                                    {sIdx === 0 && <label style={{ fontSize: 12, color: '#666' }}>&nbsp;</label>}
                                    {kalem.malzemeSatirlari.length > 1 && (
                                      <Button size="small" danger icon={<DeleteOutlined />} onClick={() => faturaMalzemeSatiriSil(entry.id, kalem.key, satir.key)} />
                                    )}
                                  </div>
                                </div>
                              ))}
                              <Button type="dashed" size="small" icon={<PlusOutlined />} onClick={() => faturaMalzemeSatiriEkle(entry.id, kalem.key)} style={{ marginTop: 4 }}>
                                Malzeme Ekle
                              </Button>
                            </div>
                          )}
                        </div>
                      ))}
                      <Button type="dashed" size="small" icon={<PlusOutlined />} onClick={() => faturaKalemEkle(entry.id)}>Kalem Ekle</Button>
                    </div>
                  )}
                  {entry.status === 'error' && (
                    <p style={{ color: '#ff4d4f' }}>{entry.error}</p>
                  )}
                  {entry.status === 'saved' && (
                    <p style={{ color: '#1890ff' }}>
                      Bu fatura başarıyla {entry.eslesme && entry.guncellemeSec === 'guncelle' ? 'güncellendi' : 'kaydedildi'}.
                    </p>
                  )}
                </Collapse.Panel>
              );
            })}
          </Collapse>
        )}
      </Modal>

      {/* Fatura Okuma - Hızlı Malzeme Ekleme Modalı */}
      <Modal
        title="Hızlı Malzeme Ekle"
        open={faturaHizliMalzemeOpen}
        onCancel={() => { setFaturaHizliMalzemeOpen(false); faturaHizliMalzemeForm.resetFields(); }}
        onOk={() => faturaHizliMalzemeForm.submit()}
        zIndex={1100}
      >
        <Form form={faturaHizliMalzemeForm} layout="vertical" onFinish={faturaHizliMalzemeKaydet}>
          <Form.Item name="ad" label="Ad" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="marka" label="Marka">
            <Input />
          </Form.Item>
          <Form.Item name="aciklama" label="Açıklama">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item name="malzeme_tipi_id" label="Tip">
            <Select options={malzemeTipleri} />
          </Form.Item>
          <Form.Item name="birim_id" label="Birim">
            <Select options={birimler} />
          </Form.Item>
          <Form.Item name="kritik_seviye" label="Kritik Seviye">
            <InputNumber style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>

      {/* Fatura Okuma - Hızlı Cari Ekleme Modalı */}
      <Modal
        title="Hızlı Cari Ekle"
        open={faturaHizliCariOpen}
        onCancel={() => { setFaturaHizliCariOpen(false); faturaHizliCariForm.resetFields(); }}
        onOk={() => faturaHizliCariForm.submit()}
        zIndex={1100}
      >
        <Form form={faturaHizliCariForm} layout="vertical" onFinish={faturaHizliCariKaydet}>
          <Form.Item name="ad" label="Ad" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="tip_idler" label="Tip" rules={[{ required: true }]}>
            <Select mode="multiple" options={cariTipleriFatura} placeholder="Bir veya birden fazla tip seçin" />
          </Form.Item>
          <Form.Item name="telefon" label="Telefon">
            <Input />
          </Form.Item>
          <Form.Item name="email" label="Email">
            <Input />
          </Form.Item>
          <Form.Item name="adres" label="Adres">
            <Input />
          </Form.Item>
        </Form>
      </Modal>

      <BirimDonusumModal
        open={donusumOpen}
        malzeme={donusumMalzeme}
        birimler={birimler}
        birimlerRaw={birimlerRaw}
        onClose={() => { setDonusumOpen(false); setDonusumMalzeme(null); }}
        onSaved={async () => {
          try {
            const r = await malzemeBirimDonusumleriAPI.getAllSon();
            setBirimDonusumleri(r.data || []);
          } catch {}
        }}
      />

      <Modal
        title={vadeData.kayit ? `Alış #${vadeData.kayit.id} — Vade & Eşleşme Detayı` : 'Vade Detayı'}
        open={vadeModalOpen}
        onCancel={() => setVadeModalOpen(false)}
        footer={<Button onClick={() => setVadeModalOpen(false)}>Kapat</Button>}
        width={isMobile ? '95vw' : 800}
      >
        {vadeData.vadeler.length === 0 ? (
          <div style={{ padding: 20, textAlign: 'center', color: '#999' }}>
            Vade kaydı yok (alış tutarı 0 veya henüz yüklenmedi)
          </div>
        ) : (
          vadeData.vadeler.map(vade => (
            <Card key={vade.id} size="small" style={{ marginBottom: 12 }}
              title={`Taksit ${vade.taksit_no} — Vade: ${dayjs(vade.vade_tarihi).format('DD.MM.YYYY')}`}
              extra={
                <Tag color={vade.durum === 'kapali' ? 'green' : vade.durum === 'kismi' ? 'orange' : 'red'}>
                  {vade.durum.toUpperCase()}
                </Tag>
              }
            >
              <Row gutter={12} style={{ marginBottom: 8 }}>
                <Col span={8}><Statistic title="Tutar" value={vade.tutar} precision={2} suffix="₺" /></Col>
                <Col span={8}><Statistic title="Ödenen" value={Number(vade.tutar) - Number(vade.kalan_tutar)} precision={2} suffix="₺" valueStyle={{ color: '#3f8600' }} /></Col>
                <Col span={8}><Statistic title="Kalan" value={vade.kalan_tutar} precision={2} suffix="₺" valueStyle={{ color: '#cf1322' }} /></Col>
              </Row>
              {vade.eslesmeler.length > 0 && (
                <Table
                  size="small"
                  dataSource={vade.eslesmeler}
                  rowKey="id"
                  pagination={false}
                  columns={[
                    { title: 'Eşleşme Tarihi', dataIndex: 'eslesme_tarihi', render: (v) => v ? dayjs(v).format('DD.MM.YYYY') : '-' },
                    { title: 'Ödeme ID', dataIndex: 'kaynak_id', render: (v) => <a onClick={() => navigate(`/odemeler?duzenle=${v}`)}>#{v}</a> },
                    { title: 'İşlem Tarihi', dataIndex: 'islem_tarihi', render: (v) => v ? dayjs(v).format('DD.MM.YYYY') : '-' },
                    { title: 'Kasa', dataIndex: 'kasa_ad' },
                    { title: 'Tutar', dataIndex: 'tutar', align: 'right', render: (v) => <strong>{formatPara(v)}</strong> },
                  ]}
                />
              )}
            </Card>
          ))
        )}
      </Modal>
    </div>
  );
}

export default Alislar;
